import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, setDoc, updateDoc, arrayUnion, collection, addDoc, serverTimestamp, increment } from 'firebase/firestore';
import { signInWithPopup, createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import { auth, db, googleProvider } from '../firebaseConfig';
import Footer from './Footer';
import Header from './Header';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { FaCheck, FaChartLine, FaTrophy, FaMicrophone, FaClipboardList, FaBullseye, FaChevronDown, FaCheckCircle, FaTimesCircle, FaStar, FaGlobe, FaExclamationTriangle } from 'react-icons/fa';
import { trackFunnelEvent } from '../utils/AnalyticsService';
import { trackGA4Event } from '../utils/GA4Analytics';
import { gradeEmail, gradePassage } from '../utils/scoringUtils';
import { evaluateOffline, evaluatePassageReconstructionRubric, evaluateEmailWritingRubric } from '../utils/offlineScoring';
import { evaluateWithAI } from '../utils/geminiScoring';

const normalizeString = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str
    .toLowerCase()
    .replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const isAnswerMatching = (userAns, correctAns) => {
  if (!correctAns) return false;
  const uClean = normalizeString(userAns);
  const cClean = normalizeString(correctAns);
  if (uClean === cClean) return true;
  
  const uNoThe = uClean.replace(/^the\s+/, '');
  const cNoThe = cClean.replace(/^the\s+/, '');
  if (uNoThe.length > 0 && uNoThe === cNoThe) return true;

  return false;
};

const ResultPage = () => {
  const { state } = useLocation();
  const {
    isPractice, section, questions, // For practice mode
    testId, typingResults, sentenceAnswers, fillAnswers, jumbledAnswers, passageInputs, emailInput, questions: allQuestions // For full test mode
  } = state || {};

  const navigate = useNavigate();

  useEffect(() => {
    document.body.classList.add('notranslate');
    return () => {
      document.body.classList.remove('notranslate');
    };
  }, []);

  // Redirect if no state is provided (e.g. direct url access or refresh)
  useEffect(() => {
    if (!state) {
      navigate('/dashboard', { replace: true });
    }
  }, [state, navigate]);
  const [score, setScore] = useState(0);
  const [maxScore, setMaxScore] = useState(80);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  // Core values derived for UI styling
  const isSingleSectionMode = results.length === 1 || state?.section || state?.isSingleSection;
  const isPracticeMode = isPractice || state?.mode === 'speaking_practice';
  const isBelowCutoff = (isPracticeMode || isSingleSectionMode) ? (((score / (maxScore || 1)) * 100) < 60) : (score < 65);
  
  // Analytics Growth Features
  const [isLocked, setIsLocked] = useState(true); // Default to locked immediately to prevent score leak
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [isLoginView, setIsLoginView] = useState(false);
  const [authError, setAuthError] = useState('');
  const [hasEbookAccess, setHasEbookAccess] = useState(false);
  const [hasPremiumAccess, setHasPremiumAccess] = useState(false);
  const [currency, setCurrency] = useState('INR');

  useEffect(() => {
    const getDetectedCurrency = () => {
      const country = sessionStorage.getItem('ga4_user_country');
      const browserLang = navigator.language || '';
      const sessionLocale = sessionStorage.getItem('versant_locale');
      
      if (window.location.pathname.startsWith('/es') || country === 'Spain' || browserLang.startsWith('es') || sessionLocale === 'es') {
        return 'EUR';
      }
      const highPayingUSDCounteries = ['United States', 'United Kingdom', 'Canada', 'Japan', 'Singapore', 'Australia', 'Brazil', 'South Korea'];
      if (highPayingUSDCounteries.includes(country)) {
        return 'USD';
      }
      if (country === 'India') {
        return 'INR';
      }
      return 'USD';
    };
    setCurrency(getDetectedCurrency());
  }, []);
  
  const dataSavedRef = React.useRef(false);
  const pendingResultRef = React.useRef(null);

  // Helper to save result to Firestore for a specific user object
  const executeSaveToFirestore = async (user, resultData, targetTestId) => {
    if (dataSavedRef.current) return;
    try {
      dataSavedRef.current = true;
      const userResultsRef = collection(db, 'users', user.uid, 'testResults');
      await addDoc(userResultsRef, {
        ...resultData,
        timestamp: new Date().toISOString(),
        createdAt: serverTimestamp()
      });

      // Also ensure main user document has testsCompleted and lastScore updated
      const userRef = doc(db, 'users', user.uid);
      const scoreVal = resultData.totalScore !== undefined ? resultData.totalScore : (resultData.score || 0);
      await updateDoc(userRef, {
        testsCompleted: increment(1),
        lastScore: scoreVal,
        lastActivity: serverTimestamp()
      }).catch(e => console.log("Non-critical user doc update:", e));

      console.log("Result saved successfully to Firestore for user:", user.uid);
      sessionStorage.removeItem('pending_guest_result');
    } catch (error) {
      console.error("Error saving result to Firestore:", error);
      dataSavedRef.current = false; // Reset so retry can succeed
    }
  };

  useEffect(() => {
    // Track result viewed for Google Ads and GA4
    if (window.gtag) {
      window.gtag('event', 'result_viewed');
    }
    trackFunnelEvent('result_viewed');

    // Strictly verify Firebase Auth state. 
    // This allows logged in users to unblur, and forces guests to hit the wall.
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setIsLocked(false);
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const premium = userData.paidTests === true || userData.hasPaid === true || userData.isPremium === true;
            setHasPremiumAccess(premium);
            setHasEbookAccess(userData.hasPurchasedEbook === true || premium);
          }
        } catch (e) {
          console.error("Error reading credentials on ResultPage:", e);
        }

        // Auto-save any pending test result as soon as user authentication is confirmed!
        if (pendingResultRef.current && !dataSavedRef.current) {
          const { resultData, testId: tId } = pendingResultRef.current;
          executeSaveToFirestore(user, resultData, tId);
        }
      } else {
        setIsLocked(true);
        setHasPremiumAccess(false);
        setHasEbookAccess(false);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!loading) {
      trackGA4Event('result_page_view', { score: score || 0, test_id: testId || 1 });
      trackFunnelEvent('score_generated', { score: score || 0, test_id: testId || 1 });
    }
  }, [loading, score, testId]);

  // Reusable Save Function
  const saveResult = React.useCallback(async (resultData, tId) => {
    const targetTestId = tId || '1';

    // Store in pending ref for async auth resolution
    pendingResultRef.current = { resultData, testId: targetTestId };

    // Store in sessionStorage as backup for guest user signup
    sessionStorage.setItem('pending_guest_result', JSON.stringify({
      resultData,
      testId: targetTestId,
      timestamp: Date.now()
    }));

    const user = auth.currentUser;
    if (user && !dataSavedRef.current) {
      await executeSaveToFirestore(user, resultData, targetTestId);
    }
  }, []);

  const handleGoogleSignIn = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      const userRef = doc(db, 'users', user.uid);
      const docSnap = await getDoc(userRef);
      if (!docSnap.exists()) {
        await setDoc(userRef, {
          email: user.email,
          name: user.displayName || 'Student',
          provider: 'google',
          testAccess: { 1: true }, // Ensure test 1 is free
          createdAt: serverTimestamp()
        });
      }

      sessionStorage.setItem('guest_unlocked', 'true');
      setIsLocked(false);

      // Instantly save result to their newly created persistent account
      if (score > 0) {
        saveResult({
          testId: testId || '1',
          totalScore: score,
          sections: results,
          type: state?.mode === 'speaking' ? 'speaking_full' : 'full_test'
        }, testId || '1');
      }
    } catch (err) {
      // User cancelled the popup or an error occurred. Keep wall locked.
      console.error("Google Auth failed:", err.code, err.message);
      // Do NOT unlock — user must successfully authenticate.
      if (err.code !== 'auth/popup-closed-by-user' && err.code !== 'auth/cancelled-popup-request') {
        setAuthError('Google Sign-In failed. Please try email instead.');
      }
    }
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      let user;
      if (isLoginView) {
        const result = await signInWithEmailAndPassword(auth, authEmail, authPassword);
        user = result.user;
      } else {
        const result = await createUserWithEmailAndPassword(auth, authEmail, authPassword);
        user = result.user;
        const userRef = doc(db, 'users', user.uid);
        await setDoc(userRef, {
          email: user.email,
          name: authEmail.split('@')[0], // simple fallback name
          provider: 'email',
          testAccess: { 1: true }, // Ensure test 1 is free
          createdAt: serverTimestamp()
        });
      }

      sessionStorage.setItem('guest_unlocked', 'true');
      setIsLocked(false);

      if (score > 0) {
        saveResult({
          testId: testId || '1',
          totalScore: score,
          sections: results,
          type: state?.mode === 'speaking' ? 'speaking_full' : 'full_test'
        }, testId || '1');
      }
    } catch (err) {
      setAuthError(err.message.replace('Firebase: ', ''));
    }
  };

  const handleNativeShare = async () => {
    const shareData = {
      title: 'My Versant AI Score',
      text: `I just scored an incredible ${score}/80 on the AI Versant Simulator! 🔥 Can you beat my score? Try it for free here:`,
      url: 'https://versantpro.com'
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.error('Share failed:', err);
      }
    } else {
      // Fallback
      navigator.clipboard.writeText(`${shareData.text} \n${shareData.url}`);
      alert("Score copied to clipboard! You can paste it anywhere to share.");
    }
  };

  // --- RECALCULATORS ---
  const recalculateReadingWritingOverall = (newResults) => {
    const typingSec = newResults.find(r => r.section === 'Typing');
    const sentenceSec = newResults.find(r => r.section === 'Sentence Completion');
    const fillSec = newResults.find(r => r.section === 'Fill in the Blanks');
    const jumbledSec = newResults.find(r => r.section === 'Jumbled Words');
    const passageSec = newResults.find(r => r.section === 'Passage Reconstruction');
    const emailSec = newResults.find(r => r.section === 'Email Writing');

    const scoreTyping = typingSec ? (typingSec.score || 20) : 20;
    const scoreSentence = sentenceSec ? (sentenceSec.score || 0) : 0;
    const scoreFill = fillSec ? (fillSec.score || 0) : 0;
    const scoreJumbled = jumbledSec ? (jumbledSec.score || 0) : 0;
    const scorePassage = passageSec ? (passageSec.score || 0) : 0;
    const scoreEmail = emailSec ? (emailSec.score || 0) : 0;

    const subTyping = scoreTyping;
    const subGrammar = 20 + Math.round(((scoreSentence + scoreFill) / 20) * 60);
    const subPassage = 20 + Math.round((scorePassage / 30) * 60);
    const subExpression = 20 + Math.round(((scoreEmail + scoreJumbled) / 25) * 60);

    const normalizedVersantScore = Math.round((subTyping + subGrammar + subPassage + subExpression) / 4);
    setScore(normalizedVersantScore);

    const allDone = !newResults.some(r => r.aiPending);
    if (allDone && !dataSavedRef.current) {
      saveResult({
        testId: testId || 'unknown',
        totalScore: normalizedVersantScore,
        sections: newResults,
        type: 'full_test'
      }, testId || 'unknown');
    }
  };

  const recalculateSpeakingOverall = (newResults) => {
    const isAllDone = !newResults.some(r => r.aiPending);
    if (!isAllDone) return;

    const totalCalculated = newResults.reduce((sum, g) => sum + (g.score || 0), 0);
    const totalMax = newResults.reduce((sum, g) => sum + g.total, 0);

    if (isPracticeMode) {
      setScore(totalCalculated);
      setMaxScore(totalMax);
    } else {
      const normalizedVersantScore = Math.round(20 + (totalCalculated / 630) * 60);
      setScore(normalizedVersantScore);
      setMaxScore(80);

      if (!dataSavedRef.current) {
        saveResult({
          testId: state.testId || 'unknown_speaking',
          totalScore: normalizedVersantScore,
          sections: newResults,
          type: 'speaking_full'
        }, state.testId || 'unknown_speaking');
      }
    }
  };

  const recalculateVeptOverall = (currentResults) => {
    if (currentResults.some(r => r.aiPending)) return;

    if (currentResults.length === 1 || state?.section || state?.isSingleSection) {
      const singleSection = currentResults[0];
      if (singleSection) {
        const totalPossible = singleSection.total || (singleSection.questions ? singleSection.questions.length * 10 : 60);
        const earned = singleSection.score || 0;
        setScore(earned);
        setMaxScore(totalPossible);
        if (!dataSavedRef.current) {
          saveResult({
            testId: testId || 'unknown_vept_single',
            totalScore: earned,
            sections: currentResults,
            type: 'vept_single_section'
          }, testId || 'unknown_vept_single');
        }
        return;
      }
    }

    const {
      sentenceCompletionAnswers = [],
      dictationAnswers = [],
      passageReconstructionAnswers = []
    } = state;

    const partA = currentResults.find(r => r.section === 'Part A: Read Aloud');
    const partB = currentResults.find(r => r.section === 'Part B: Repeats');
    const partC = currentResults.find(r => r.section === 'Part C: Sentence Builds');
    const partD = currentResults.find(r => r.section === 'Part D: Conversations');
    const partE = currentResults.find(r => r.section === 'Part E: Typing');
    const partF = currentResults.find(r => r.section === 'Part F: Sentence Completion');
    const partG = currentResults.find(r => r.section === 'Part G: Dictation');
    const partH = currentResults.find(r => r.section === 'Part H: Passage Reconstruction');
    const partI = currentResults.find(r => r.section === 'Part I: Summary & Opinion');

    const scoreA = partA ? (partA.score || 0) : 0;
    const scoreB = partB ? (partB.score || 0) : 0;
    const scoreC = partC ? (partC.score || 0) : 0;
    const scoreD = partD ? (partD.score || 0) : 0;
    const scoreE = partE ? (partE.score || 0) : 0;
    const scoreF = partF ? (partF.score || 0) : 0;
    const scoreG = partG ? (partG.score || 0) : 0;
    const scoreH = partH ? (partH.score || 0) : 0;
    const scoreI = partI ? (partI.score || 0) : 0;

    const rawSpeaking = scoreA + scoreB + scoreC;
    const countSpeakingQs = ((partA?.questions?.length || 0) + (partB?.questions?.length || 0) + (partC?.questions?.length || 0));
    const maxSpeaking = (countSpeakingQs * 10) || 1;
    const subSpeaking = 20 + Math.round((rawSpeaking / maxSpeaking) * 60);

    const rawListening = scoreB + scoreC + scoreD + scoreG;
    const countListeningQs = ((partB?.questions?.length || 0) + (partC?.questions?.length || 0) + (partD?.questions?.length || 0));
    const maxListening = (countListeningQs * 10) + (dictationAnswers.length * 10) || 1;
    const subListening = 20 + Math.round((rawListening / maxListening) * 60);

    const rawReading = scoreA + scoreF + scoreI;
    const maxReading = ((partA?.questions?.length || 0) * 10 || 1) + (sentenceCompletionAnswers.length) + 10;
    const subReading = 20 + Math.round((rawReading / maxReading) * 60);

    const rawWriting = scoreE + scoreG + scoreH + scoreI;
    const maxWriting = 80 + (dictationAnswers.length * 10) + (passageReconstructionAnswers.length * 10) + 10;
    const subWriting = 20 + Math.round((rawWriting / maxWriting) * 60);

    const finalScore = Math.round((subSpeaking + subListening + subReading + subWriting) / 4);
    setScore(finalScore);

    if (!dataSavedRef.current) {
      saveResult({
        testId: testId || 'unknown_vept',
        totalScore: finalScore,
        sections: currentResults,
        type: 'vept_full'
      }, testId || 'unknown_vept');
    }
  };

  // --- SUB-EVALUATION FUNCTIONS (ASYNCHRONOUS BACKGROUND) ---
  const evaluateEmailAI = async (promptText, userAnswer, isPracticeVal) => {
    try {
      let emailResult = await evaluateWithAI('email', {
        prompt: promptText,
        response: userAnswer
      });

      if (emailResult.error) {
        throw new Error(emailResult.details);
      }

      setResults(prevResults => {
        const nextResults = [...prevResults];
        const idx = nextResults.findIndex(r => r.section === 'Email Writing' || r.section === 'Part I: Summary & Opinion');
        const targetIdx = idx !== -1 ? idx : nextResults.findIndex(r => r.section?.toLowerCase().includes('email'));
        if (targetIdx !== -1) {
          nextResults[targetIdx] = {
            ...nextResults[targetIdx],
            aiPending: false,
            aiError: false,
            score: emailResult.score,
            aiAnalysis: emailResult,
            questions: [{
              question: promptText,
              userAnswer: userAnswer || 'No Answer',
              correctAnswer: promptText,
              score: emailResult.score,
              aiFeedback: emailResult
            }]
          };
        }
        if (isPracticeVal) {
          setScore(emailResult.score);
          setMaxScore(10);
        } else {
          recalculateReadingWritingOverall(nextResults);
        }
        return nextResults;
      });
    } catch (err) {
      console.warn("AI Grading failed for Email Writing, falling back to offline:", err);
      const offlineGrading = evaluateEmailWritingRubric(userAnswer, promptText);
      const emailResult = {
        score: offlineGrading.score,
        feedback: offlineGrading.aiFeedback.grammar_feedback,
        cefr_level: "B1",
        tone_analysis: "N/A",
        corrections: [],
        ideal_response: "A structured business email.",
        aiError: true
      };
      setResults(prevResults => {
        const nextResults = [...prevResults];
        const idx = nextResults.findIndex(r => r.section === 'Email Writing' || r.section === 'Part I: Summary & Opinion');
        const targetIdx = idx !== -1 ? idx : nextResults.findIndex(r => r.section?.toLowerCase().includes('email'));
        if (targetIdx !== -1) {
          nextResults[targetIdx] = {
            ...nextResults[targetIdx],
            aiPending: false,
            aiError: true,
            score: emailResult.score,
            aiAnalysis: emailResult,
            questions: [{
              question: promptText,
              userAnswer: userAnswer || 'No Answer',
              correctAnswer: promptText,
              score: emailResult.score,
              aiFeedback: emailResult
            }]
          };
        }
        if (isPracticeVal) {
          setScore(emailResult.score);
          setMaxScore(10);
        } else {
          recalculateReadingWritingOverall(nextResults);
        }
        return nextResults;
      });
    }
  };

  const evaluatePassagesAI = async (passageData, isPracticeVal, practiceQuestions) => {
    try {
      let passageResults = await evaluateWithAI('passage_batch', passageData);

      if (passageResults.error || !Array.isArray(passageResults)) {
        throw new Error(passageResults.details || "Batch result error");
      }

      setResults(prevResults => {
        const nextResults = [...prevResults];
        const idx = nextResults.findIndex(r => r.section === 'Passage Reconstruction');
        if (idx !== -1) {
          nextResults[idx] = {
            ...nextResults[idx],
            aiPending: false,
            aiError: false,
            aiAnalysis: passageResults,
            questions: nextResults[idx].questions.map((q, i) => ({
              ...q,
              score: passageResults[i]?.score || 0,
              aiFeedback: passageResults[i]
            })),
            score: passageResults.reduce((sum, res) => sum + (res.score || 0), 0)
          };
        }
        if (isPracticeVal) {
          const totalScore = passageResults.reduce((sum, res) => sum + (res.score || 0), 0);
          setScore(totalScore);
          setMaxScore(practiceQuestions.length * 10);
        } else {
          recalculateReadingWritingOverall(nextResults);
        }
        return nextResults;
      });
    } catch (err) {
      console.warn("AI Grading failed for Passage Reconstruction batch, falling back to offline:", err);
      const fallbackResults = passageData.map((p, index) => {
        const offlineGrading = evaluatePassageReconstructionRubric(p.response, p.original);
        return {
          score: offlineGrading.score,
          grammar_feedback: offlineGrading.aiFeedback.grammar_feedback,
          ideal_response: p.original,
          missing_points: [],
          aiError: true
        };
      });
      setResults(prevResults => {
        const nextResults = [...prevResults];
        const idx = nextResults.findIndex(r => r.section === 'Passage Reconstruction');
        if (idx !== -1) {
          nextResults[idx] = {
            ...nextResults[idx],
            aiPending: false,
            aiError: true,
            aiAnalysis: fallbackResults,
            questions: nextResults[idx].questions.map((q, i) => ({
              ...q,
              score: fallbackResults[i]?.score || 0,
              aiFeedback: fallbackResults[i]
            })),
            score: fallbackResults.reduce((sum, res) => sum + (res.score || 0), 0)
          };
        }
        if (isPracticeVal) {
          const totalScore = fallbackResults.reduce((sum, res) => sum + (res.score || 0), 0);
          setScore(totalScore);
          setMaxScore(practiceQuestions.length * 10);
        } else {
          recalculateReadingWritingOverall(nextResults);
        }
        return nextResults;
      });
    }
  };

  const evaluateSpeakingAI = async (r) => {
    try {
      const testIdToUse = r.testId || state.testId || "1";
      const specificTest = await import('../data/speakingTest.json');
      const rootJSON = specificTest.default || specificTest;
      const currentMockData = rootJSON[testIdToUse] || rootJSON["1"] || {};

      let originalStoryText = r.questionText;
      let callType = '';
      let callPayload = {};

      if (r.section === 'storyRetelling') {
        callType = 'story_retelling';
        const storyData = currentMockData.storyRetelling?.find(q =>
          r.questionText && q.audioText && (
            r.questionText.includes(q.audioText.substring(0, 15)) ||
            q.audioText.includes(r.questionText.substring(0, 15))
          )
        );
        originalStoryText = storyData ? storyData.audioText : r.questionText;
        callPayload = { audioText: originalStoryText, response: r.transcript };
      } else {
        callType = 'open_question';
        callPayload = { question: r.questionText, response: r.transcript };
      }

      let aiFeedback = await evaluateWithAI(callType, callPayload);

      if (aiFeedback.error) {
        throw new Error(aiFeedback.details);
      }

      setResults(prevResults => {
        const nextResults = prevResults.map(section => {
          const isStoryMatch = section.section === 'Story Retelling' && r.section === 'storyRetelling';
          const isOpenMatch = section.section === 'Open Questions' && r.section === 'openQuestions';
          if (isStoryMatch) {
            const mappedQs = section.questions.map(q => {
              if (q.question === r.questionText) {
                return {
                  ...q,
                  score: typeof aiFeedback.score === 'number' ? aiFeedback.score : 0,
                  aiPending: false,
                  aiError: false,
                  aiFeedback: {
                    grammar_feedback: aiFeedback.feedback || aiFeedback.grammar_feedback || "",
                    missing_points: aiFeedback.missing_points || [],
                    ideal_response: aiFeedback.ideal_response || originalStoryText
                  }
                };
              }
              return q;
            });
            return {
              ...section,
              questions: mappedQs,
              aiPending: mappedQs.some(q => q.aiPending),
              aiError: mappedQs.some(q => q.aiError),
              score: mappedQs.reduce((sum, q) => sum + (q.score || 0), 0)
            };
          }
          if (isOpenMatch) {
            const mappedQs = section.questions.map(q => {
              if (q.question === r.questionText) {
                return {
                  ...q,
                  score: typeof aiFeedback.score === 'number' ? aiFeedback.score : 0,
                  aiPending: false,
                  aiError: false,
                  aiFeedback: {
                    grammar_feedback: aiFeedback.feedback || aiFeedback.grammar_feedback || "",
                    ideal_response: aiFeedback.ideal_response || "A strong spoken response."
                  }
                };
              }
              return q;
            });
            return {
              ...section,
              questions: mappedQs,
              aiPending: mappedQs.some(q => q.aiPending),
              aiError: mappedQs.some(q => q.aiError),
              score: mappedQs.reduce((sum, q) => sum + (q.score || 0), 0)
            };
          }
          return section;
        });

        recalculateSpeakingOverall(nextResults);
        return nextResults;
      });
    } catch (err) {
      console.warn(`AI Grading failed for Speaking section ${r.section}, falling back to offline:`, err);
      const testIdToUse = r.testId || state.testId || "1";
      const specificTest = await import('../data/speakingTest.json');
      const rootJSON = specificTest.default || specificTest;
      const currentMockData = rootJSON[testIdToUse] || rootJSON["1"] || {};
      const offlineGrading = evaluateOffline(r.section, r, currentMockData);

      const aiFeedback = {
        score: offlineGrading.score,
        feedback: offlineGrading.aiFeedback.grammar_feedback,
        missing_points: offlineGrading.aiFeedback.missing_points || [],
        ideal_response: offlineGrading.aiFeedback.ideal_response || r.questionText,
        aiError: true
      };

      setResults(prevResults => {
        const nextResults = prevResults.map(section => {
          const isStoryMatch = section.section === 'Story Retelling' && r.section === 'storyRetelling';
          const isOpenMatch = section.section === 'Open Questions' && r.section === 'openQuestions';
          if (isStoryMatch) {
            const mappedQs = section.questions.map(q => {
              if (q.question === r.questionText) {
                return {
                  ...q,
                  score: (typeof aiFeedback.score === 'number') ? aiFeedback.score : 0,
                  aiPending: false,
                  aiError: true,
                  aiFeedback: {
                    grammar_feedback: aiFeedback.feedback || aiFeedback.grammar_feedback || "",
                    missing_points: aiFeedback.missing_points || [],
                    ideal_response: aiFeedback.ideal_response || r.questionText
                  }
                };
              }
              return q;
            });
            return {
              ...section,
              questions: mappedQs,
              aiPending: mappedQs.some(q => q.aiPending),
              aiError: true,
              score: mappedQs.reduce((sum, q) => sum + (q.score || 0), 0)
            };
          }
          if (isOpenMatch) {
            const mappedQs = section.questions.map(q => {
              if (q.question === r.questionText) {
                return {
                  ...q,
                  score: (typeof aiFeedback.score === 'number') ? aiFeedback.score : 0,
                  aiPending: false,
                  aiError: true,
                  aiFeedback: {
                    grammar_feedback: aiFeedback.feedback || aiFeedback.grammar_feedback || "",
                    ideal_response: aiFeedback.ideal_response || "A strong spoken response."
                  }
                };
              }
              return q;
            });
            return {
              ...section,
              questions: mappedQs,
              aiPending: mappedQs.some(q => q.aiPending),
              aiError: true,
              score: mappedQs.reduce((sum, q) => sum + (q.score || 0), 0)
            };
          }
          return section;
        });

        recalculateSpeakingOverall(nextResults);
        return nextResults;
      });
    }
  };

  const evaluateVEPTPassagesAI = async (passageData, passageReconstructionAnswers) => {
    try {
      let passageAIEvals = await evaluateWithAI('passage_batch', passageData);

      if (passageAIEvals.error || !Array.isArray(passageAIEvals)) {
        throw new Error(passageAIEvals.details || "Batch result error");
      }

      setResults(prevResults => {
        const nextResults = [...prevResults];
        const idx = nextResults.findIndex(r => r.section === 'Part H: Passage Reconstruction');
        if (idx !== -1) {
          nextResults[idx] = {
            ...nextResults[idx],
            aiPending: false,
            aiError: false,
            score: passageAIEvals.reduce((sum, res) => sum + (res.score || 0), 0),
            questions: nextResults[idx].questions.map((q, i) => ({
              ...q,
              score: passageAIEvals[i]?.score || 0,
              aiFeedback: passageAIEvals[i]
            }))
          };
        }
        recalculateVeptOverall(nextResults);
        return nextResults;
      });
    } catch (err) {
      console.warn("AI Grading failed for VEPT Passage Reconstruction, falling back to offline:", err);
      const fallbackResults = (passageReconstructionAnswers || []).map(p => {
        const offlineGrading = evaluatePassageReconstructionRubric(p.userAnswer, p.originalPassage);
        return {
          score: offlineGrading.score,
          grammar_feedback: offlineGrading.aiFeedback.grammar_feedback,
          ideal_response: p.originalPassage,
          missing_points: [],
          aiError: true
        };
      });
      setResults(prevResults => {
        const nextResults = [...prevResults];
        const idx = nextResults.findIndex(r => r.section === 'Part H: Passage Reconstruction');
        if (idx !== -1) {
          nextResults[idx] = {
            ...nextResults[idx],
            aiPending: false,
            aiError: true,
            score: fallbackResults.reduce((sum, res) => sum + (res.score || 0), 0),
            questions: nextResults[idx].questions.map((q, i) => ({
              ...q,
              score: fallbackResults[i]?.score || 0,
              aiFeedback: fallbackResults[i]
            }))
          };
        }
        recalculateVeptOverall(nextResults);
        return nextResults;
      });
    }
  };

  const evaluateVEPTSummaryOpinionAI = async (summaryOpinionAnswer) => {
    try {
      let summaryOpinionEval = await evaluateWithAI('vept_summary_opinion', {
        passage: summaryOpinionAnswer?.passage || "",
        prompt: summaryOpinionAnswer?.prompt || "",
        response: summaryOpinionAnswer?.userAnswer || ""
      });

      if (summaryOpinionEval.error) {
        throw new Error(summaryOpinionEval.details);
      }

      setResults(prevResults => {
        const nextResults = [...prevResults];
        const idx = nextResults.findIndex(r => r.section === 'Part I: Summary & Opinion');
        if (idx !== -1) {
          nextResults[idx] = {
            ...nextResults[idx],
            aiPending: false,
            aiError: false,
            score: summaryOpinionEval.score || 0,
            questions: [{
              question: summaryOpinionAnswer?.prompt || '',
              userAnswer: summaryOpinionAnswer?.userAnswer || '',
              feedback: summaryOpinionEval.feedback || '',
              aiFeedback: summaryOpinionEval
            }],
            aiAnalysis: summaryOpinionEval
          };
        }
        recalculateVeptOverall(nextResults);
        return nextResults;
      });
    } catch (err) {
      console.warn("AI Grading failed for VEPT Summary & Opinion, falling back to offline:", err);
      const offlineGrading = evaluateEmailWritingRubric(summaryOpinionAnswer?.userAnswer || "", summaryOpinionAnswer?.prompt || "");
      const summaryOpinionEval = {
        score: offlineGrading.score,
        feedback: offlineGrading.aiFeedback.grammar_feedback,
        cefr_level: "B1",
        corrections: [],
        ideal_response: "A clear summary and opinion.",
        aiError: true
      };
      setResults(prevResults => {
        const nextResults = [...prevResults];
        const idx = nextResults.findIndex(r => r.section === 'Part I: Summary & Opinion');
        if (idx !== -1) {
          nextResults[idx] = {
            ...nextResults[idx],
            aiPending: false,
            aiError: true,
            score: summaryOpinionEval.score || 0,
            questions: [{
              question: summaryOpinionAnswer?.prompt || '',
              userAnswer: summaryOpinionAnswer?.userAnswer || '',
              feedback: summaryOpinionEval.feedback || '',
              aiFeedback: summaryOpinionEval
            }],
            aiAnalysis: summaryOpinionEval
          };
        }
        recalculateVeptOverall(nextResults);
        return nextResults;
      });
    }
  };

  const handleRetrySection = async (sectionName) => {
    setResults(prev => prev.map(r => {
      if (r.section === sectionName) {
        return { ...r, aiPending: true, aiError: false };
      }
      return r;
    }));

    if (sectionName === 'Email Writing') {
      await evaluateEmailAI(state.questions[39]?.prompt, state.emailInput, false);
    } else if (sectionName === 'Passage Reconstruction') {
      if (isPracticeMode) {
        const { questions: practiceQuestions, answers: practiceAnswers } = state;
        const pData = practiceQuestions.map(q => ({
          original: q.sentences,
          response: practiceAnswers[q.id] || ''
        }));
        await evaluatePassagesAI(pData, true, practiceQuestions);
      } else {
        const pData = (state.passageInputs || []).map((input, index) => ({
          original: state.questions?.filter(q => q.sentences)[index]?.sentences || "",
          response: input || ""
        }));
        await evaluatePassagesAI(pData, false, state.questions?.filter(q => q.sentences));
      }
    } else if (sectionName === 'Story Retelling') {
      const storyResponse = state.testResponses?.find(r => r.section === 'storyRetelling');
      if (storyResponse) await evaluateSpeakingAI(storyResponse);
    } else if (sectionName === 'Open Questions') {
      const openQResponse = state.testResponses?.find(r => r.section === 'openQuestions');
      if (openQResponse) await evaluateSpeakingAI(openQResponse);
    } else if (sectionName === 'Part H: Passage Reconstruction') {
      const passageData = (state.passageReconstructionAnswers || []).map(p => ({
        original: p.originalPassage || "",
        response: p.userAnswer || ""
      }));
      await evaluateVEPTPassagesAI(passageData, state.passageReconstructionAnswers);
    } else if (sectionName === 'Part I: Summary & Opinion') {
      await evaluateVEPTSummaryOpinionAI(state.summaryOpinionAnswer);
    }
  };

  // --- BACKGROUND AI RUNNER ---
  useEffect(() => {
    if (!state) return;

    const runAIEvaluation = async () => {
      if (state.aiAnalysisDone) return;

      const { evaluateWithAI } = await import('../utils/geminiScoring');
      const { 
        evaluateOffline, 
        evaluatePassageReconstructionRubric, 
        evaluateEmailWritingRubric 
      } = await import('../utils/offlineScoring');
      
      console.log("Starting AI Evaluation...");

      // --- WRITING PRACTICE SECTIONS AI EVALUATION ---
      if (isPracticeMode && (section === 'emailWriting' || section === 'passageReconstruction')) {
        const { questions: practiceQuestions, answers: practiceAnswers } = state;
        if (section === 'emailWriting') {
          const q = practiceQuestions[0];
          const userAnswer = practiceAnswers[q.id] || '';
          await evaluateEmailAI(q.prompt, userAnswer, true);
        } else if (section === 'passageReconstruction') {
          const passageData = practiceQuestions.map(q => ({
            original: q.sentences,
            response: practiceAnswers[q.id] || ''
          }));
          await evaluatePassagesAI(passageData, true, practiceQuestions);
        }
        return;
      }

      // --- SPEAKING TEST AI EVALUATION — SINGLE BATCH CALL ---
      // All 5 items (3 story retelling + 2 open questions) sent in 1 API call.
      // Previously: 5 separate calls = burst spike risk.
      // Now: 1 call per test = 100 users = 100 calls total (well within paid tier limits).
      if (state.mode === 'speaking' || state.mode === 'speaking_practice') {
        const testResponses = state.testResponses || [];
        const speakingTest = await import('../data/speakingTest.json');
        const rootJSON = speakingTest.default || speakingTest;

        // Build batch payload — collect all AI-evaluated items
        const batchItems = [];
        const batchMeta = []; // track which response each item belongs to

        for (const r of testResponses) {
          if (r.section === 'storyRetelling') {
            const testIdToUse = r.testId || state.testId || "1";
            const currentMockData = rootJSON[testIdToUse] || rootJSON["1"] || {};
            const storyData = currentMockData.storyRetelling?.find(q =>
              r.questionText && q.audioText && (
                r.questionText.includes(q.audioText.substring(0, 15)) ||
                q.audioText.includes(r.questionText.substring(0, 15))
              )
            );
            const audioText = storyData ? storyData.audioText : r.questionText;
            batchItems.push({ type: 'story_retelling', audioText, response: r.transcript || '' });
            batchMeta.push({ r, originalStoryText: audioText });
          } else if (r.section === 'openQuestions') {
            batchItems.push({ type: 'open_question', question: r.questionText, response: r.transcript || '' });
            batchMeta.push({ r, originalStoryText: r.questionText });
          }
        }

        if (batchItems.length > 0) {
          try {
            const batchResults = await evaluateWithAI('speaking_batch', batchItems);

            // batchResults is an array matching batchItems order
            if (Array.isArray(batchResults) && !batchResults[0]?.error) {
              setResults(prevResults => {
                let nextResults = prevResults.map(section => ({ ...section }));

                batchMeta.forEach((meta, idx) => {
                  const aiFeedback = batchResults[idx];
                  if (!aiFeedback) return;
                  const { r } = meta;

                  nextResults = nextResults.map(section => {
                    const isStoryMatch = section.section === 'Story Retelling' && r.section === 'storyRetelling';
                    const isOpenMatch = section.section === 'Open Questions' && r.section === 'openQuestions';

                    if (isStoryMatch || isOpenMatch) {
                      const mappedQs = section.questions.map(q => {
                        if (q.question === r.questionText) {
                          return {
                            ...q,
                            score: typeof aiFeedback.score === 'number' ? aiFeedback.score : 5,
                            aiPending: false,
                            aiError: false,
                            aiFeedback: {
                              grammar_feedback: aiFeedback.feedback || aiFeedback.grammar_feedback || '',
                              missing_points: aiFeedback.missing_points || [],
                              ideal_response: aiFeedback.ideal_response || meta.originalStoryText
                            }
                          };
                        }
                        return q;
                      });
                      return {
                        ...section,
                        questions: mappedQs,
                        aiPending: mappedQs.some(q => q.aiPending),
                        aiError: mappedQs.some(q => q.aiError),
                        score: mappedQs.reduce((sum, q) => sum + (q.score || 0), 0)
                      };
                    }
                    return section;
                  });
                });

                recalculateSpeakingOverall(nextResults);
                return nextResults;
              });
            } else {
              // Batch failed — fall back to offline grading for all items
              throw new Error(batchResults?.[0]?.details || 'Batch evaluation failed');
            }
          } catch (err) {
            console.warn('Speaking batch AI failed, falling back to TRUE offline grading for all:', err);
            // ⚠️ FIX (Aug 2026 incident): previously this called evaluateSpeakingAI(r),
            // which itself makes ANOTHER live Gemini API call before falling back to
            // offline — firing up to 5 extra individual requests per failed test at
            // exactly the moment Gemini was already rate-limiting. That amplification
            // was a major contributor to the suspension-triggering burst.
            // This now goes straight to evaluateOffline() — zero additional API calls.
            const speakingTestData = await import('../data/speakingTest.json');
            const rootJSON = speakingTestData.default || speakingTestData;

            setResults(prevResults => {
              let nextResults = prevResults.map(section => ({ ...section }));

              batchMeta.forEach(({ r }) => {
                const testIdToUse = r.testId || state.testId || "1";
                const currentMockData = rootJSON[testIdToUse] || rootJSON["1"] || {};
                const offlineGrading = evaluateOffline(r.section, r, currentMockData);
                const aiFeedback = {
                  score: offlineGrading.score,
                  feedback: offlineGrading.aiFeedback.grammar_feedback,
                  missing_points: offlineGrading.aiFeedback.missing_points || [],
                  ideal_response: offlineGrading.aiFeedback.ideal_response || r.questionText,
                  aiError: true
                };

                nextResults = nextResults.map(section => {
                  const isStoryMatch = section.section === 'Story Retelling' && r.section === 'storyRetelling';
                  const isOpenMatch = section.section === 'Open Questions' && r.section === 'openQuestions';
                  if (!isStoryMatch && !isOpenMatch) return section;

                  const mappedQs = section.questions.map(q => {
                    if (q.question !== r.questionText) return q;
                    return {
                      ...q,
                      score: (typeof aiFeedback.score === 'number') ? aiFeedback.score : 0,
                      aiPending: false,
                      aiError: true,
                      aiFeedback: {
                        grammar_feedback: aiFeedback.feedback || '',
                        missing_points: aiFeedback.missing_points || [],
                        ideal_response: aiFeedback.ideal_response || r.questionText
                      }
                    };
                  });
                  return {
                    ...section,
                    questions: mappedQs,
                    aiPending: false,
                    aiError: true,
                    score: mappedQs.reduce((sum, q) => sum + (q.score || 0), 0)
                  };
                });
              });

              recalculateSpeakingOverall(nextResults);
              return nextResults;
            });
          }
        }
        return;
      }

      // --- VEPT UNIFIED TEST AI EVALUATION ---
      if (state.mode === 'vept') {
        const { passageReconstructionAnswers = [], summaryOpinionAnswer } = state;
        const passageData = passageReconstructionAnswers.map(p => ({
          original: p.originalPassage || "",
          response: p.userAnswer || ""
        }));
        const pPromise = evaluateVEPTPassagesAI(passageData, passageReconstructionAnswers);
        const sPromise = evaluateVEPTSummaryOpinionAI(summaryOpinionAnswer);
        // Sequential: passage first, then summary opinion
        await pPromise;
        await sPromise;
        return;
      }

      // --- READING/WRITING FULL TEST AI EVALUATION ---
      if (state.emailInput !== undefined || state.passageInputs || (!state.mode && state.questions)) {
        const pData = (state.passageInputs || []).map((input, index) => ({
          original: state.questions?.filter(q => q.sentences)[index]?.sentences || "",
          response: input || ""
        }));
        const pPromise = evaluatePassagesAI(pData, false, state.questions?.filter(q => q.sentences));
        const emailPrompt = state.questions?.[39]?.prompt || "";
        const ePromise = evaluateEmailAI(emailPrompt, state.emailInput || "", false);
        // Sequential: passages first, then email
        await pPromise;
        await ePromise;
      }
    };

    runAIEvaluation();
  }, [state]);

  // --- INITIAL RENDERING FORMATTER (SYNCHRONOUS ON MOUNT) ---
  useEffect(() => {
    if (!state) {
      setLoading(false);
      return;
    }

    if (dataSavedRef.current) return;

    // --- 1. Speaking Test Formatting ---
    if (state.mode === 'speaking' || state.mode === 'speaking_practice') {
      const initSpeakingResults = async () => {
        try {
          const testResponses = state.testResponses || [];
          const specificTest = await import('../data/speakingTest.json');
          const rootJSON = specificTest.default || specificTest;
          
          let initialSpeakingResponses = [];
          for (let r of testResponses) {
            const testIdToUse = r.testId || state.testId || "1";
            const currentMockData = rootJSON[testIdToUse] || rootJSON["1"] || {};
            
            if (r.section === 'storyRetelling' || r.section === 'openQuestions') {
              initialSpeakingResponses.push({
                ...r,
                aiPending: true,
                aiError: false,
                aiFeedback: null,
                score: null
              });
            } else {
              const offlineGrading = evaluateOffline(r.section, r, currentMockData);
              initialSpeakingResponses.push({
                ...r,
                aiFeedback: offlineGrading.aiFeedback,
                score: offlineGrading.score || 0
              });
            }
          }

          const getGroup = (secName, displayName, maxScorePerQ) => {
            const qs = initialSpeakingResponses.filter(r => r.section === secName).map(r => ({
              question: r.questionText,
              userAnswer: r.transcript,
              correctAnswer: r.correctAnswer || r.targetText || r.answer || r.questionText,
              score: r.score,
              aiFeedback: r.aiFeedback,
              aiPending: r.aiPending,
              aiError: r.aiError
            }));
            if (qs.length === 0) return null;
            return {
              section: displayName,
              questions: qs,
              score: qs.some(q => q.aiPending) ? null : qs.reduce((sum, q) => sum + (q.score || 0), 0),
              total: qs.length * maxScorePerQ,
              aiPending: qs.some(q => q.aiPending),
              aiError: qs.some(q => q.aiError),
              aiGraded: true
            };
          };

          const groupedResults = [
            getGroup('readAloud', 'Read Aloud', 10),
            getGroup('repeats', 'Repeats', 10),
            getGroup('shortAnswer', 'Short Answer', 10),
            getGroup('sentenceBuilds', 'Sentence Builds', 10),
            getGroup('storyRetelling', 'Story Retelling', 10),
            getGroup('openQuestions', 'Open Questions', 10)
          ].filter(Boolean);

          setResults(groupedResults);
          
          const totalCalculated = groupedResults.reduce((sum, g) => sum + (g.score || 0), 0);
          const totalMax = groupedResults.reduce((sum, g) => sum + g.total, 0);

          if (isPracticeMode) {
            setScore(totalCalculated);
            setMaxScore(totalMax);
          } else {
            const allDone = !groupedResults.some(r => r.aiPending);
            const normalizedVersantScore = Math.round(20 + (totalCalculated / 630) * 60);
            setScore(allDone ? normalizedVersantScore : 20);
            setMaxScore(80);
          }
          
          setLoading(false);
        } catch (e) {
          console.error("Init speaking error:", e);
          setLoading(false);
        }
      };
      initSpeakingResults();
      return;
    }

    // --- 2. VEPT Unified Test Formatting ---
    if (state.mode === 'vept') {
      const initVeptResults = async () => {
        try {
          const {
            typingResults,
            sentenceCompletionAnswers = [],
            dictationAnswers = [],
            passageReconstructionAnswers = [],
            summaryOpinionAnswer,
            speakingResponses = []
          } = state;

          const specificTest = await import('../data/veptTests.json');
          const rootJSON = specificTest.default || specificTest;
          const currentMockData = rootJSON[testId || "1"] || rootJSON["1"] || {};

          const veptSpeakingGroups = [
            'readAloud', 'repeats', 'sentenceBuilds', 'conversations'
          ].map(sec => {
            const items = (speakingResponses || []).filter(r => r.section === sec).map(r => ({
              question: r.questionText,
              userAnswer: r.transcript || 'No Speech Recorded',
              correctAnswer: r.correctAnswer || r.questionText,
              score: r.score
            }));
            if (items.length === 0) return null;
            const titleMap = {
              readAloud: 'Part A: Read Aloud',
              repeats: 'Part B: Repeats',
              sentenceBuilds: 'Part C: Sentence Builds',
              conversations: 'Part D: Conversations'
            };
            const totalQCount = (currentMockData[sec] && Array.isArray(currentMockData[sec])) ? currentMockData[sec].length : items.length;
            return {
              section: titleMap[sec] || sec,
              questions: items,
              score: items.reduce((sum, i) => sum + (i.score || 0), 0),
              total: totalQCount * 10
            };
          }).filter(Boolean);

          const initialVeptResults = [
            ...veptSpeakingGroups,
            typingResults ? {
              section: 'Part E: Typing',
              wpm: typingResults?.wpm || 0,
              accuracy: typingResults?.accuracy || 0,
              score: 20 + Math.min(60, Math.round(((typingResults?.wpm || 0) * ((typingResults?.accuracy || 0) / 100) / 80) * 60)),
              total: 80
            } : null,
            (sentenceCompletionAnswers && sentenceCompletionAnswers.length > 0) ? {
              section: 'Part F: Sentence Completion',
              questions: (sentenceCompletionAnswers || []).map(a => ({
                question: a.questionText,
                userAnswer: a.userAnswer,
                correctAnswer: a.correctAnswer,
                isCorrect: a.isCorrect,
                score: a.score
              })),
              score: (sentenceCompletionAnswers || []).filter(a => a.isCorrect).length,
              total: (sentenceCompletionAnswers || []).length
            } : null,
            (dictationAnswers && dictationAnswers.length > 0) ? {
              section: 'Part G: Dictation',
              questions: (dictationAnswers || []).map(a => ({
                question: a.questionText,
                userAnswer: a.userAnswer,
                correctAnswer: a.correctAnswer || a.questionText,
                score: a.score
              })),
              score: (dictationAnswers || []).reduce((sum, a) => sum + (a.score || 0), 0),
              total: (dictationAnswers || []).length * 10
            } : null,
            (passageReconstructionAnswers && passageReconstructionAnswers.length > 0) ? {
              section: 'Part H: Passage Reconstruction',
              questions: (passageReconstructionAnswers || []).map(p => ({
                question: p.originalPassage,
                userAnswer: p.userAnswer,
                correctAnswer: p.correctAnswer || p.originalPassage,
                score: null,
                aiPending: true,
                aiError: false
              })),
              score: null,
              total: passageReconstructionAnswers.length * 10,
              aiPending: true,
              aiError: false
            } : null,
            (summaryOpinionAnswer && summaryOpinionAnswer.userAnswer) ? {
              section: 'Part I: Summary & Opinion',
              questions: [{
                question: summaryOpinionAnswer?.prompt || '',
                userAnswer: summaryOpinionAnswer?.userAnswer || '',
                feedback: null,
                aiPending: true,
                aiError: false
              }],
              score: null,
              total: 10,
              aiPending: true,
              aiError: false
            } : null
          ].filter(Boolean);

          setResults(initialVeptResults);
          if (initialVeptResults.length === 1 || state?.section || state?.isSingleSection) {
            const single = initialVeptResults[0];
            if (single) {
              const earned = single.score || 0;
              const totalPossible = single.total || (single.questions ? single.questions.length * 10 : 60);
              setScore(earned);
              setMaxScore(totalPossible);

              if (!initialVeptResults.some(r => r.aiPending) && !dataSavedRef.current) {
                saveResult({
                  testId: testId || 'unknown_vept_single',
                  totalScore: earned,
                  sections: initialVeptResults,
                  type: 'vept_single_section'
                }, testId || 'unknown_vept_single');
              }
            } else {
              setScore(0);
              setMaxScore(80);
            }
          } else {
            setScore(0);
            setMaxScore(80);
          }
          setLoading(false);
        } catch (e) {
          console.error("Init vept error:", e);
          setLoading(false);
        }
      };
      initVeptResults();
      return;
    }

    // --- 3. Practice Hub Mode Formatting ---
    if (isPractice) {
      if (section === 'emailWriting' || section === 'passageReconstruction') {
        const { questions: practiceQuestions, answers: practiceAnswers } = state;
        let initialResults = [];

        if (section === 'emailWriting') {
          const q = practiceQuestions[0];
          initialResults = [{
            section: 'Email Writing',
            questions: [{
              question: q.prompt,
              userAnswer: practiceAnswers[q.id] || '',
              score: null,
              aiFeedback: null
            }],
            score: null,
            total: 10,
            aiPending: true,
            aiError: false
          }];
          setResults(initialResults);
          setScore(0);
          setMaxScore(10);
        } else if (section === 'passageReconstruction') {
          initialResults = [{
            section: 'Passage Reconstruction',
            questions: practiceQuestions.map(q => ({
              question: q.sentences,
              userAnswer: practiceAnswers[q.id] || '',
              score: null,
              aiFeedback: null
            })),
            score: null,
            total: practiceQuestions.length * 10,
            aiPending: true,
            aiError: false
          }];
          setResults(initialResults);
          setScore(0);
          setMaxScore(practiceQuestions.length * 10);
        }
        setLoading(false);
        return;
      }

      const { questions: practiceQuestions, answers: practiceAnswers } = state;
      let calculatedScore = 0;
      let maxPracticeScore = practiceQuestions.length;
      let detailedResults = [];

      if (section === 'typing') {
        const q = practiceQuestions[0];
        const referenceText = q.paragraph;
        const userText = practiceAnswers[q.id] || '';

        const cleanRef = referenceText.trim();
        const cleanUser = userText.trim();

        const refWords = cleanRef.split(/\s+/).filter(w => w.length > 0);
        const userWords = cleanUser.split(/\s+/).filter(w => w.length > 0);

        const m = refWords.length;
        const n = userWords.length;
        const dp = Array(m + 1).fill().map(() => Array(n + 1).fill(0));

        for (let i = 1; i <= m; i++) {
          for (let j = 1; j <= n; j++) {
            const wordA = refWords[i - 1].toLowerCase().replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, "");
            const wordB = userWords[j - 1].toLowerCase().replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, "");
            if (wordA === wordB) {
              dp[i][j] = dp[i - 1][j - 1] + 1;
            } else {
              dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
            }
          }
        }

        const rightWords = dp[m][n];
        const wrongWords = Math.max(0, userWords.length - rightWords);
        const keystrokes = userText.length;

        const timeInMinutes = 1.0;
        const grossWpm = Math.round((keystrokes / 5) / timeInMinutes);
        const wpm = Math.max(0, Math.round(grossWpm - (wrongWords / timeInMinutes)));
        const accuracy = userWords.length > 0 ? Math.min(100, Math.round((rightWords / userWords.length) * 100)) : 0;

        calculatedScore = wpm;
        maxPracticeScore = 80;
        detailedResults = [{
          question: q.paragraph,
          userAnswer: userText || 'No Answer',
          correctAnswer: q.paragraph,
          isCorrect: accuracy >= 90
        }];

        setScore(wpm);
        setMaxScore(80);

        setResults([{
          section: 'Typing',
          questions: detailedResults,
          score: wpm,
          wpm: wpm,
          accuracy: accuracy,
          rightWords,
          wrongWords,
          keystrokes,
          userAnswer: userText,
          paragraph: q.paragraph,
          total: 80
        }]);
      } else {
        detailedResults = practiceQuestions.map((q, index) => {
          const userAnswer = practiceAnswers[q.id] || '';
          let isCorrect = false;
          if (section === 'repeats') {
            isCorrect = isAnswerMatching(userAnswer, q.sentence);
          } else if (section === 'sentenceBuilds') {
            isCorrect = isAnswerMatching(userAnswer, q.sentence);
          } else if (section === 'shortQuestions') {
            isCorrect = q.answers && q.answers.some(ans => isAnswerMatching(userAnswer, ans));
          } else {
            isCorrect = isAnswerMatching(userAnswer, q.answer);
          }

          if (isCorrect) calculatedScore++;

          return {
            question: q.question || q.sentence || q.jumbled || 'Question',
            userAnswer: userAnswer || 'No Answer',
            correctAnswer: q.answer || q.sentence || (q.answers ? q.answers.join(' / ') : ''),
            isCorrect
          };
        });

        setScore(calculatedScore);
        setMaxScore(maxPracticeScore);

        const sectionDisplayName = section === 'repeats' ? 'Repeats'
          : section === 'sentenceBuilds' ? 'Sentence Builds'
          : section === 'shortQuestions' ? 'Short Answer'
          : section.charAt(0).toUpperCase() + section.slice(1);

        setResults([{
          section: sectionDisplayName,
          questions: detailedResults,
          score: calculatedScore,
          total: maxPracticeScore
        }]);
      }
      setLoading(false);
      return;
    }

    // --- 4. Reading/Writing Full Test Formatting ---
    let detailedResults = [];
    let typingScoreValue = 20;

    if (typingResults) {
      const wpm = typingResults.wpm || 0;
      const accuracy = typingResults.accuracy || 0;
      typingScoreValue = 20 + Math.min(60, Math.round((wpm * (accuracy / 100) / 80) * 60));
      detailedResults.push({
        section: 'Typing',
        wpm: typingResults.wpm,
        accuracy: typingResults.accuracy,
        score: typingScoreValue,
        rightWords: typingResults.rightWords || 0,
        wrongWords: typingResults.wrongWords || 0,
        keystrokes: typingResults.keystrokes || 0,
        userAnswer: typingResults.typingInput || '',
        paragraph: typingResults.paragraph || ''
      });
    }

    const sentenceQs = allQuestions.slice(1, 11);
    const sentenceResults = sentenceQs.map((q, i) => {
      const userAnswer = sentenceAnswers[i] || 'Not Answered';
      const isCorrect = q.answer && isAnswerMatching(userAnswer, q.answer);
      return { question: q.question, userAnswer, correctAnswer: q.answer, isCorrect };
    });
    const sentenceCorrectCount = sentenceResults.filter(r => r.isCorrect).length;
    detailedResults.push({ section: 'Sentence Completion', questions: sentenceResults, score: sentenceCorrectCount, total: sentenceQs.length });

    const fillQs = allQuestions.slice(11, 21);
    const fillResults = fillQs.map((q, i) => {
      const userAnswer = fillAnswers[i] || 'Not Answered';
      const isCorrect = q.answer && isAnswerMatching(userAnswer, q.answer);
      return { question: q.question, userAnswer, correctAnswer: q.answer, isCorrect };
    });
    const fillCorrectCount = fillResults.filter(r => r.isCorrect).length;
    detailedResults.push({ section: 'Fill in the Blanks', questions: fillResults, score: fillCorrectCount, total: fillQs.length });

    const jumbledQs = allQuestions.slice(21, 36);
    const jumbledResults = jumbledQs.map((q, i) => {
      const userAnswer = jumbledAnswers[i] || 'Not Answered';
      const isCorrect = q.answer && isAnswerMatching(userAnswer, q.answer);
      return { question: q.jumbled, userAnswer, correctAnswer: q.answer, isCorrect };
    });
    const jumbledCorrectCount = jumbledResults.filter(r => r.isCorrect).length;
    detailedResults.push({ section: 'Jumbled Words', questions: jumbledResults, score: jumbledCorrectCount, total: jumbledQs.length });

    const passageQs = allQuestions.slice(36, 39);
    const passageResults = passageQs.map((q, i) => ({
      question: q.sentences,
      userAnswer: passageInputs[i] || 'Not Answered',
      score: null,
      aiPending: true,
      aiError: false
    }));
    detailedResults.push({
      section: 'Passage Reconstruction',
      questions: passageResults,
      score: null,
      total: 30,
      aiPending: true,
      aiError: false
    });

    const emailQ = allQuestions[39];
    detailedResults.push({
      section: 'Email Writing',
      questions: [{
        question: emailQ.prompt,
        userAnswer: emailInput || 'Not Answered',
        score: null,
        feedback: null,
        aiPending: true,
        aiError: false
      }],
      score: null,
      total: 10,
      aiPending: true,
      aiError: false
    });

    setResults(detailedResults);
    setScore(0);
    setMaxScore(80);
    setLoading(false);
  }, [state, saveResult]);


  if (!state) {
    return null;
  }

  if (loading) {
    return (
      <div className="bg-gray-100 min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex flex-col items-center justify-center p-4">
          <div className="bg-white p-8 rounded-2xl shadow-xl max-w-sm w-full text-center">
            <div className="w-16 h-16 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin mx-auto mb-6"></div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Analyzing Results</h2>
            <p className="text-gray-500 text-sm">Our system is instantly grading your spoken responses...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const isAnyAiPending = Array.isArray(results) && results.some(r => r.aiPending);

  return (
    <div className="bg-gray-100 min-h-screen flex flex-col">
      <Header />
      {isPractice && false ? (
        // --- Practice Mode Result Display ---
        <main className="max-w-4xl mx-auto py-12 px-4">
          <div className="bg-white rounded-lg shadow-xl p-8">
            <h1 className="text-3xl font-bold text-center text-gray-800 mb-2">
              Practice Results: {section.charAt(0).toUpperCase() + section.slice(1)}
            </h1>

            <div className="text-center mb-8">
              <p className="text-lg font-medium text-gray-700">Your Score</p>
              {section === 'typing' ? (
                <div className="flex justify-center space-x-8">
                  <div><span className="text-5xl font-bold text-green-600">{results.wpm}</span> WPM</div>
                  <div><span className="text-5xl font-bold text-green-600">{results.accuracy}%</span> Accuracy</div>
                </div>
              ) : (
                <p className="text-6xl font-bold text-green-600">{score} / {questions.length}</p>
              )}
            </div>

            <div className="space-y-4">
              {Array.isArray(results) && results.map((result, index) => (
                <div key={index} className={`p-4 rounded-lg border ${result.isCorrect ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <p className="font-semibold text-gray-700 mb-2">Q{index + 1}: {result.question?.replace('___', '...') || result.question?.replace(/\//g, ' ')}</p>
                  <p className={`text-sm ${result.isCorrect ? 'text-green-700' : 'text-red-700'}`}>
                    Your Answer: <span className="font-bold">{result.userAnswer}</span>
                    {result.isCorrect ? ' (Correct)' : ` (Incorrect)`}
                  </p>
                  {!result.isCorrect && (
                    <p className="text-sm text-gray-600">
                      Correct Answer: <span className="font-bold">{result.correctAnswer}</span>
                    </p>
                  )}
                </div>
              ))}
            </div>

            <div className="text-center mt-8 space-x-4">
              <Link
                to={`/practice/${section}`}
                state={{ fromResult: true }}
                className="inline-block bg-green-600 text-white font-bold py-3 px-8 rounded-lg hover:bg-green-700 transition-colors"
              >
                Next Test
              </Link>
              <Link
                to="/practice"
                className="inline-block text-green-600 font-bold py-3 px-8"
              >
                Back to Practice Hub
              </Link>
            </div>
          </div>
        </main>
      ) : (
        // --- Full Test Result Display ---
        <main className="max-w-6xl mx-auto py-12 px-4 sm:px-6 lg:px-8 animate-fade-in">
          <div className={`bg-white rounded-[2rem] shadow-2xl overflow-hidden border border-gray-100 transition-all ${isLocked ? 'blur-md pointer-events-none select-none' : ''}`}>
            {/* Header Section */}
            <div className="bg-gradient-to-br from-indigo-900 via-indigo-800 to-indigo-900 p-10 sm:p-14 text-white text-center relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent"></div>
              <div className="relative z-10 flex flex-col items-center">
                <div className="w-24 h-24 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center mb-6 shadow-[0_0_40px_rgba(99,102,241,0.4)] border border-white/20">
                  <FaTrophy className="text-5xl text-yellow-400 drop-shadow-lg" />
                </div>
                <h1 className="text-4xl md:text-5xl font-black mb-3 tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white to-indigo-200">
                  {isPracticeMode ? `${section.charAt(0).toUpperCase() + section.slice(1)} Practice Report` : 'Performance Report'}
                </h1>
                <p className="text-indigo-200 text-lg flex items-center gap-2 font-medium">
                  <FaClipboardList className="text-indigo-300" /> {isPracticeMode ? `Practice Pack: ${section}` : `Test ID: ${testId}`} <span className="text-indigo-500 mx-2">•</span> {state?.mode === 'speaking' ? 'Speaking & Listening' : 'Reading & Writing'}
                </p>
              </div>
            </div>
 
            <div className="p-6 sm:p-12 bg-gray-50/50">
              {/* Top Analytics Row */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
                {/* Score Card */}
                <div className={`lg:col-span-4 rounded-3xl p-8 border shadow-[0_8px_30px_rgb(0,0,0,0.04)] relative overflow-hidden flex flex-col items-center justify-center group hover:-translate-y-1 transition-transform duration-300 ${isBelowCutoff ? 'bg-red-50/50 border-red-100' : 'bg-emerald-50/50 border-emerald-100'}`}>
                  <div className="absolute -top-10 -right-10 p-4 opacity-5 group-hover:scale-110 transition-transform duration-500">
                    <FaBullseye className={isBelowCutoff ? "text-[12rem] text-red-900" : "text-[12rem] text-emerald-900"} />
                  </div>
                  <h3 className={`${isBelowCutoff ? 'text-red-400' : 'text-emerald-500'} font-bold tracking-[0.2em] uppercase text-xs mb-2 relative z-10`}>{isPracticeMode ? 'Your Score' : 'Your Versant Score'}</h3>
                  <div className="relative z-10 flex items-baseline gap-2 mb-4">
                    {isAnyAiPending ? (
                      <div className="flex flex-col items-center justify-center min-h-[5.5rem] py-2 relative z-10">
                        <div className="w-8 h-8 border-3 border-indigo-100 border-t-indigo-600 rounded-full animate-spin mb-2 shadow-sm"></div>
                        <span className="text-[10px] font-black tracking-wider text-indigo-500 uppercase animate-pulse">Grading Spoken/Written Parts...</span>
                      </div>
                    ) : (
                      <>
                        <span className={`text-8xl font-black text-transparent bg-clip-text bg-gradient-to-br drop-shadow-sm ${isBelowCutoff ? 'from-red-600 to-orange-600' : 'from-emerald-600 to-teal-600'}`}>
                          {score}
                        </span>
                        <span className="text-2xl text-gray-400 font-bold">/ {maxScore}</span>
                      </>
                    )}
                  </div>
                  
                  {/* The MNC Hiring Cutoff Visual */}
                  {!isPracticeMode ? (
                    <div className="w-full relative z-10 mt-2 mb-2 flex flex-col items-center text-center">
                      <div className="w-full bg-gray-200 h-1.5 rounded-full overflow-hidden mb-2 relative">
                          <div className="absolute h-full bg-red-400 w-[80%] left-0"></div>
                          <div className="absolute h-full bg-emerald-400 w-[20%] right-0"></div>
                          <div className="absolute h-4 w-1 bg-gray-900 top-1/2 -translate-y-1/2 left-[81%] shadow-sm"></div>
                          <div className="absolute h-full rounded-full transition-all duration-1000 ease-out" style={{ width: `${isAnyAiPending ? 0 : (score / 80) * 100}%`, backgroundColor: score < 65 ? '#dc2626' : '#059669' }}></div >
                      </div>
                      <span className="text-xs font-bold text-gray-500 tracking-wider">TARGET MNC CUTOFF: <span className="text-gray-900">65+</span></span>
                    </div>
                  ) : (
                    <div className="w-full relative z-10 mt-4 mb-2 flex flex-col items-center text-center">
                      <div className="w-full bg-gray-200 h-1.5 rounded-full overflow-hidden mb-2 relative">
                          <div className="absolute h-full rounded-full transition-all duration-1000 ease-out bg-emerald-500" style={{ width: `${isAnyAiPending ? 0 : (score / maxScore) * 100}%` }}></div>
                      </div>
                      <span className="text-xs font-bold text-gray-500 tracking-wider">Accuracy: <span className="text-gray-900">{isAnyAiPending ? 'Calculating...' : `${Math.round((score / maxScore) * 100)}%`}</span></span>
                    </div>
                  )}

                  {!isPracticeMode && isBelowCutoff && (
                    <div className="w-full mt-5 bg-gradient-to-r from-red-600 to-rose-600 border-2 border-red-400 rounded-2xl p-4 text-center relative z-10 shadow-[0_0_30px_rgba(220,38,38,0.4)] transform hover:scale-[1.02] transition-transform animate-pulse">
                        <span className="text-yellow-300 font-black text-lg block tracking-widest uppercase drop-shadow-md">⚠️ DANGER ZONE</span>
                        <span className="text-white text-[12px] font-bold leading-relaxed block mt-2 tracking-wide uppercase">You are currently <u className="decoration-yellow-300 underline-offset-4 decoration-2">NOT READY</u> to pass top MNC interviews.</span>
                    </div>
                  )}

                  <div className="flex flex-col gap-3 mt-4 relative z-10 w-full">
                    <div className="inline-flex justify-center items-center gap-2 bg-emerald-50 text-emerald-600 border border-emerald-100 px-4 py-2 rounded-full font-bold text-sm shadow-sm">
                      <FaGlobe className="text-emerald-500" /> Global Scale of English
                    </div>
                  </div>
                </div>

                {/* Section-Wise Weightage & Cutoff Matrix Table */}
                <div className="lg:col-span-8 bg-white border border-gray-200 rounded-3xl p-6 sm:p-8 shadow-xl relative overflow-hidden">
                  <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200 flex-wrap gap-3">
                    <div>
                      <h3 className="text-xl sm:text-2xl font-black text-gray-900 tracking-tight flex items-center gap-2">
                        📊 Result At A Glance & Weightage Breakdown
                      </h3>
                      <p className="text-xs text-gray-500 mt-1 font-medium">Official Versant scoring weight distribution vs. MNC Hiring Cutoff</p>
                    </div>
                    <span className="px-4 py-1.5 bg-slate-900 text-white font-extrabold text-xs rounded-full shadow-md border border-slate-700 tracking-wide">
                      TARGET CUTOFF: 60+
                    </span>
                  </div>

                  <div className="overflow-x-auto rounded-2xl border border-gray-200 shadow-sm">
                    <table className="w-full text-left text-sm border-collapse">
                      <thead>
                        <tr className="bg-slate-900 text-slate-200 font-extrabold uppercase text-[11px] tracking-wider divide-x divide-slate-800">
                          <th className="py-3.5 px-4">Section Module</th>
                          <th className="py-3.5 px-3 text-center">Weightage</th>
                          <th className="py-3.5 px-3 text-center">Target</th>
                          <th className="py-3.5 px-3 text-center">Your Score</th>
                          <th className="py-3.5 px-4 text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 font-medium text-gray-800 bg-white">
                        {(() => {
                          const isSingleSectionMode = results.length === 1 || state?.section || state?.isSingleSection;
                          let rowsToDisplay = [];

                          if (isSingleSectionMode && results.length > 0) {
                            rowsToDisplay = results.map(resObj => {
                              const secScore = resObj.score !== null && typeof resObj.score !== 'undefined' ? resObj.score : 0;
                              const secTotal = resObj.total || (resObj.questions ? resObj.questions.length * 10 : 60);
                              return {
                                name: resObj.section || 'Practice Section',
                                weight: '100%',
                                cutoff: 60,
                                secScore,
                                secTotal
                              };
                            });
                          } else {
                            const sectionWeightMap = [
                              { key: 'Read Aloud', name: 'Part A: Read Aloud', weight: '15%', cutoff: 60, defaultTotal: 8 },
                              { key: 'Repeat Sentence', name: 'Part B: Repeat Sentence', weight: '25%', cutoff: 60, defaultTotal: 16 },
                              { key: 'Sentence Completion', name: 'Part C: Sentence Completion & Builds', weight: '15%', cutoff: 60, defaultTotal: 10 },
                              { key: 'Passage Reconstruction', name: 'Part D: Story Retelling / Passage', weight: '20%', cutoff: 60, defaultTotal: 30 },
                              { key: 'Fill in the Blanks', name: 'Part E: Open Questions / Dictation', weight: '15%', cutoff: 60, defaultTotal: 10 },
                              { key: 'Email Writing', name: 'Part F: Email Writing', weight: '10%', cutoff: 60, defaultTotal: 10 }
                            ];

                            // Check if results is a subset of sections (e.g. 1-2 sections in practice mode)
                            const attemptedResults = results.filter(r => r && (r.questions?.length > 0 || r.total > 0));
                            if (attemptedResults.length > 0 && attemptedResults.length < 6) {
                              rowsToDisplay = attemptedResults.map(resObj => {
                                const secScore = resObj.score !== null && typeof resObj.score !== 'undefined' ? resObj.score : 0;
                                const secTotal = resObj.total || (resObj.questions ? resObj.questions.length * 10 : 10);
                                return {
                                  name: resObj.section || 'Section Practice',
                                  weight: `${Math.round((1 / attemptedResults.length) * 100)}%`,
                                  cutoff: 60,
                                  secScore,
                                  secTotal
                                };
                              });
                            } else {
                              rowsToDisplay = sectionWeightMap.map(sec => {
                                const resObj = results.find(r => r.section && (r.section.toLowerCase().includes(sec.key.toLowerCase()) || sec.key.toLowerCase().includes(r.section.toLowerCase())));
                                const isPending = resObj?.aiPending;
                                const secScore = resObj ? (resObj.score !== null && typeof resObj.score !== 'undefined' ? resObj.score : (isPending ? 'Grading...' : 0)) : 0;
                                const secTotal = resObj?.total || sec.defaultTotal;
                                return {
                                  name: sec.name,
                                  weight: sec.weight,
                                  cutoff: sec.cutoff,
                                  secScore,
                                  secTotal,
                                  isPending
                                };
                              });
                            }
                          }

                          return rowsToDisplay.map((sec, idx) => {
                            const secScore = sec.secScore;
                            const secTotal = sec.secTotal;
                            const normalizedPct = typeof secScore === 'number' ? Math.round((secScore / secTotal) * 100) : null;
                            const isPass = normalizedPct !== null && normalizedPct >= 60;
                            
                            return (
                              <tr key={idx} className={`divide-x divide-gray-200 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'} hover:bg-emerald-50/40`}>
                                <td className="py-3.5 px-4 font-bold text-gray-900">{sec.name}</td>
                                <td className="py-3.5 px-3 text-center text-gray-600 font-semibold bg-gray-50/30">{sec.weight}</td>
                                <td className="py-3.5 px-3 text-center font-bold text-indigo-700">{sec.cutoff}+</td>
                                <td className="py-3.5 px-3 text-center font-extrabold text-gray-900 bg-gray-50/30">
                                  {typeof secScore === 'number' ? `${secScore} / ${secTotal}` : <span className="text-indigo-600 animate-pulse text-xs">{secScore}</span>}
                                </td>
                                <td className="py-3.5 px-4 text-center">
                                  {normalizedPct === null ? (
                                    <span className="px-2.5 py-1 bg-indigo-50 text-indigo-700 text-xs font-bold rounded-full border border-indigo-200 shadow-xs">Evaluating</span>
                                  ) : isPass ? (
                                    <span className="px-3 py-1 bg-emerald-100 text-emerald-800 text-xs font-extrabold rounded-full border border-emerald-300 shadow-xs">🟢 Passed</span>
                                  ) : (
                                    <span className="px-3 py-1 bg-rose-100 text-rose-800 text-xs font-extrabold rounded-full border border-rose-300 shadow-xs">🔴 Needs Work</span>
                                  )}
                                </td>
                              </tr>
                            );
                          });
                        })()}
                        {/* Overall Composite Score Summary Row */}
                        <tr className="bg-slate-900 text-white font-extrabold divide-x divide-slate-800 border-t-2 border-slate-900">
                          <td className="py-4 px-4 uppercase tracking-wider text-xs font-black">OVERALL VERSANT COMPOSITE SCORE</td>
                          <td className="py-4 px-3 text-center text-emerald-400 text-xs font-black">100%</td>
                          <td className="py-4 px-3 text-center text-indigo-300 text-xs font-black">60+ Target</td>
                          <td className="py-4 px-3 text-center text-yellow-300 text-base font-black">{score} / {maxScore}</td>
                          <td className="py-4 px-4 text-center">
                            {(isSingleSectionMode ? ((score / (maxScore || 1)) >= 0.60) : score >= 60) ? (
                              <span className="px-3 py-1 bg-emerald-500 text-slate-950 font-black text-xs rounded-full uppercase tracking-wider shadow-md">OVERALL PASS</span>
                            ) : (
                              <span className="px-3 py-1 bg-rose-500 text-white font-black text-xs rounded-full uppercase tracking-wider shadow-md">NEEDS PRACTICE</span>
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* DYNAMIC SMART RECOMMENDATION ENGINE (CONTEXT-AWARE CONVERSION CARD) */}
                {(() => {
                  if (results.length === 0) return null;

                  const sectionWeightMap = [
                    { key: 'Sentence Completion', name: 'Sentence Completion', packId: 'sentenceCompletion', packName: 'Sentence Completion Pack' },
                    { key: 'Fill in the Blanks', name: 'Dictation & Fill in Blanks', packId: 'fillBlanks', packName: 'Dictation & Fill Blanks Pack' },
                    { key: 'Jumbled Words', name: 'Jumbled Words', packId: 'jumbledWords', packName: 'Jumbled Words Mastery' },
                    { key: 'Passage Reconstruction', name: 'Passage Reconstruction', packId: 'passageReconstruction', packName: 'Passage Reconstruction Pro' },
                    { key: 'Email Writing', name: 'Email Writing', packId: 'emailWriting', packName: 'Email Writing Mastery' }
                  ];

                  // Evaluate sections scores
                  let sectionScores = [];
                  sectionWeightMap.forEach(sec => {
                    const r = results.find(item => item.section === sec.key);
                    if (r && typeof r.score === 'number' && r.total) {
                      const pct = Math.round((r.score / r.total) * 100);
                      sectionScores.push({ ...sec, pct, score: r.score, total: r.total });
                    }
                  });

                  if (sectionScores.length === 0) return null;

                  // Sort by lowest percentage score
                  sectionScores.sort((a, b) => a.pct - b.pct);

                  const lowest = sectionScores[0];
                  const weakCount = sectionScores.filter(s => s.pct < 60).length;

                  let recType = 'section'; // 'section' | 'full' | 'ebook'
                  if (weakCount >= 2) {
                    recType = 'full';
                  } else if (weakCount === 0 || lowest.pct >= 70) {
                    recType = 'ebook';
                  }

                  return (
                    <div className="lg:col-span-12 mt-4">
                      <div className="bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900 border-2 border-emerald-500/40 rounded-3xl p-6 sm:p-10 shadow-[0_0_50px_rgba(16,185,129,0.15)] text-left relative overflow-hidden">
                        <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none"></div>

                        <div className="flex items-center gap-2 mb-3">
                          <span className="px-3 py-1 bg-emerald-500/20 border border-emerald-400/30 text-emerald-300 text-[11px] font-black uppercase tracking-widest rounded-full">
                            🎯 PERSONALIZED AI RECOMMENDATION FOR YOU
                          </span>
                        </div>

                        {recType === 'section' && (
                          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                            <div className="space-y-2 max-w-2xl">
                              <h3 className="text-2xl sm:text-3xl font-black text-white leading-tight">
                                Your Biggest Improvement Opportunity: <span className="text-emerald-400">{lowest.name}</span> ({lowest.pct}%)
                              </h3>
                              <p className="text-slate-300 text-sm sm:text-base leading-relaxed font-light">
                                Your performance indicates that <strong className="text-white font-semibold">{lowest.name}</strong> is pulling your overall Versant score down. Unlocking targeted 20 practice drills for this section will help you cross the MNC cutoff.
                              </p>
                              <div className="flex items-center gap-3 pt-2">
                                <span className="text-emerald-400 font-bold text-sm">
                                  Recommended: {lowest.packName}
                                </span>
                                <span className="text-slate-400 text-xs">• One-time payment</span>
                              </div>
                            </div>

                            <div className="flex-shrink-0 w-full md:w-auto flex flex-col items-center md:items-end gap-2">
                              <span className="text-3xl font-black text-white">
                                {currency === 'INR' ? '₹299' : currency === 'EUR' ? '€2.99' : '$2.99'}
                              </span>
                              <Link
                                to="/pricing"
                                className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-emerald-400 to-teal-300 hover:from-emerald-300 hover:to-teal-200 text-slate-950 font-extrabold rounded-xl shadow-[0_0_30px_rgba(16,185,129,0.3)] transition-all hover:scale-[1.02] active:scale-95 text-center text-sm"
                              >
                                Unlock {lowest.name} Pack (₹299) &rarr;
                              </Link>
                            </div>
                          </div>
                        )}

                        {recType === 'full' && (
                          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                            <div className="space-y-2 max-w-2xl">
                              <h3 className="text-2xl sm:text-3xl font-black text-white leading-tight">
                                Multiple Weak Sections Detected ({weakCount} Sections Below 60%)
                              </h3>
                              <p className="text-slate-300 text-sm sm:text-base leading-relaxed font-light">
                                Section-wise practice alone won't be enough. To pass top corporate Versant cutoffs (Amazon, Deloitte, etc.), you need full exam simulation with complete AI voice analytics.
                              </p>
                              <div className="flex items-center gap-3 pt-2">
                                <span className="text-emerald-400 font-bold text-sm">
                                  Recommended: 45-Day Pass ({currency === 'INR' ? '₹699' : currency === 'EUR' ? '€6.99' : '$6.99'}) or Lifetime VIP ({currency === 'INR' ? '₹1,449' : currency === 'EUR' ? '€14.99' : '$14.99'})
                                </span>
                                <span className="text-slate-400 text-xs">• All 38 Premium Mock Exams</span>
                              </div>
                            </div>

                            <div className="flex-shrink-0 w-full md:w-auto flex flex-col items-center md:items-end gap-2">
                              <span className="text-3xl font-black text-white">
                                Starting {currency === 'INR' ? '₹699' : currency === 'EUR' ? '€6.99' : '$6.99'}
                              </span>
                              <Link
                                to="/pricing"
                                className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-emerald-400 to-teal-300 hover:from-emerald-300 hover:to-teal-200 text-slate-950 font-extrabold rounded-xl shadow-[0_0_30px_rgba(16,185,129,0.3)] transition-all hover:scale-[1.02] active:scale-95 text-center text-sm"
                              >
                                Unlock 38 Premium Tests &rarr;
                              </Link>
                            </div>
                          </div>
                        )}

                        {recType === 'ebook' && (
                          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                            <div className="space-y-2 max-w-2xl">
                              <h3 className="text-2xl sm:text-3xl font-black text-white leading-tight">
                                High Performance Score! Refine Your Exam Strategy
                              </h3>
                              <p className="text-slate-300 text-sm sm:text-base leading-relaxed font-light">
                                Your score is strong! To ensure you don't make small mistakes during actual MNC screening, review our high-scoring response templates and secret voice pacing rules.
                              </p>
                              <div className="flex items-center gap-3 pt-2">
                                <span className="text-emerald-400 font-bold text-sm">
                                  Recommended: Versant Test Mastery eBook
                                </span>
                              </div>
                            </div>

                            <div className="flex-shrink-0 w-full md:w-auto flex flex-col items-center md:items-end gap-2">
                              <span className="text-3xl font-black text-white">
                                {currency === 'INR' ? '₹199' : currency === 'EUR' ? '€2.99' : '$2.99'}
                              </span>
                              <Link
                                to="/ebook"
                                className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-amber-400 to-amber-300 text-slate-950 font-extrabold rounded-xl shadow-lg transition-all hover:scale-[1.02] active:scale-95 text-center text-sm"
                              >
                                Get Mastery eBook (₹199) &rarr;
                              </Link>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* PREMIUM RESOURCES COMPARISON GRID */}
                <div className="lg:col-span-12 mt-8 bg-white border border-gray-100 rounded-3xl p-6 sm:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] text-left">
                  <h3 className="text-xl font-black text-gray-900 tracking-tight mb-2 flex items-center gap-2">
                    ⚖️ Choose The Right Preparation Resource For Your Goal
                  </h3>
                  <p className="text-xs text-gray-500 mb-8">Compare options to see what best fits your preparation level</p>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {/* eBook Card */}
                    <div className="border border-gray-200 rounded-2xl p-5 flex flex-col justify-between hover:border-amber-400 hover:shadow-md transition-all">
                      <div>
                        <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center font-bold text-xl mb-3">📘</div>
                        <h4 className="text-base font-extrabold text-gray-900 mb-1">Versant Mastery eBook</h4>
                        <p className="text-xs text-gray-500 mb-3 min-h-[32px]">Learn strategy, secret rules & high-scoring speech templates</p>
                        <div className="text-2xl font-black text-gray-900 mb-3">{currency === 'INR' ? '₹199' : currency === 'EUR' ? '€2.99' : '$2.99'}</div>
                        <ul className="text-xs text-gray-600 space-y-2 mb-6 border-t border-gray-100 pt-3">
                          <li className="flex items-center gap-1.5">✓ 20 Full Practice Question Sets</li>
                          <li className="flex items-center gap-1.5">✓ Repeat Sentence & Story Hacks</li>
                          <li className="flex items-center gap-1.5">✓ Ideal High-Score Sample Answers</li>
                        </ul>
                      </div>
                      <Link to="/ebook" className="w-full py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-xl text-center text-xs transition-colors block">
                        Get Strategy eBook (₹199)
                      </Link>
                    </div>

                    {/* Section Pack Card */}
                    <div className="border border-indigo-200 rounded-2xl p-5 flex flex-col justify-between hover:border-indigo-400 hover:shadow-md transition-all bg-indigo-50/10">
                      <div>
                        <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-xl mb-3">🎯</div>
                        <h4 className="text-base font-extrabold text-gray-900 mb-1">Section Practice Pack</h4>
                        <p className="text-xs text-gray-500 mb-3 min-h-[32px]">Perfect if you need to improve one specific section</p>
                        <div className="text-2xl font-black text-gray-900 mb-3">{currency === 'INR' ? '₹299' : currency === 'EUR' ? '€2.99' : '$2.99'}</div>
                        <ul className="text-xs text-gray-600 space-y-2 mb-6 border-t border-indigo-100 pt-3">
                          <li className="flex items-center gap-1.5">✓ 20 Targeted Section Drills</li>
                          <li className="flex items-center gap-1.5">✓ Advanced AI Voice Analytics</li>
                          <li className="flex items-center gap-1.5">✓ Lifetime Access for 1 Section</li>
                        </ul>
                      </div>
                      <Link to="/pricing" className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl text-center text-xs transition-colors block">
                        Practice This Section →
                      </Link>
                    </div>

                    {/* 45-Day Sprint Pass Card (₹699) */}
                    <div className="border-2 border-teal-500 rounded-2xl p-5 flex flex-col justify-between hover:shadow-xl transition-all bg-teal-50/30 relative">
                      <span className="absolute -top-3 right-3 px-2.5 py-0.5 bg-teal-600 text-white text-[9px] font-black uppercase tracking-wider rounded-full">
                        🎯 Best For Upcoming Test
                      </span>
                      <div>
                        <div className="w-10 h-10 rounded-xl bg-teal-100 text-teal-800 flex items-center justify-center font-bold text-xl mb-3">⚡</div>
                        <h4 className="text-base font-extrabold text-gray-900 mb-1">45-Day Sprint Pass</h4>
                        <p className="text-xs text-gray-500 mb-3 min-h-[32px]">Best for candidates with an upcoming test in next 1–6 weeks</p>
                        <div className="text-2xl font-black text-gray-900 mb-3">{currency === 'INR' ? '₹699' : currency === 'EUR' ? '€6.99' : '$6.99'}</div>
                        <ul className="text-xs text-gray-600 space-y-2 mb-6 border-t border-teal-100 pt-3">
                          <li className="flex items-center gap-1.5">✓ <strong>All 38 Premium Mock Exams</strong></li>
                          <li className="flex items-center gap-1.5">✓ Unlimited AI Speech Evaluation</li>
                          <li className="flex items-center gap-1.5">✓ 45 Days Full Access (No Renewal)</li>
                        </ul>
                      </div>
                      <Link to="/pricing" className="w-full py-2.5 bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-xl text-center text-xs transition-colors block">
                        Get 45-Day Pass (₹699)
                      </Link>
                    </div>

                    {/* Lifetime VIP Pass Card (₹1,449) */}
                    <div className="border-2 border-emerald-500 rounded-2xl p-5 flex flex-col justify-between hover:shadow-xl transition-all bg-emerald-50/40 relative">
                      <span className="absolute -top-3 right-3 px-2.5 py-0.5 bg-emerald-700 text-white text-[9px] font-black uppercase tracking-wider rounded-full">
                        ⭐ Best Value
                      </span>
                      <div>
                        <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold text-xl mb-3">🏆</div>
                        <h4 className="text-base font-extrabold text-gray-900 mb-1">Lifetime VIP Pass</h4>
                        <p className="text-xs text-gray-500 mb-3 min-h-[32px]">Never expires • Includes eBook (₹199 value) + All Future Updates</p>
                        <div className="text-2xl font-black text-gray-900 mb-3">{currency === 'INR' ? '₹1,449' : currency === 'EUR' ? '€14.99' : '$14.99'}</div>
                        <ul className="text-xs text-gray-600 space-y-2 mb-6 border-t border-emerald-100 pt-3">
                          <li className="flex items-center gap-1.5">✓ <strong>All 38 Premium Mock Exams</strong></li>
                          <li className="flex items-center gap-1.5">✓ Unlimited AI Scoring Forever</li>
                          <li className="flex items-center gap-1.5">✓ <strong>FREE Versant Mastery eBook</strong></li>
                          <li className="flex items-center gap-1.5">✓ 7-Day Money-Back Guarantee</li>
                        </ul>
                      </div>
                      <Link to="/pricing" className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-center text-xs transition-colors block">
                        Upgrade Lifetime VIP (₹1449)
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* Detailed Breakdown */}
              <div className="mb-8">
                <h3 className="text-2xl font-black text-gray-900 mb-8 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gray-900 flex items-center justify-center text-white shadow-md">
                    <FaClipboardList />
                  </div>
                  Detailed Analysis
                </h3>

                <div className="space-y-6">
                  {Array.isArray(results) && results.map((sectionResult, index) => (
                    <div key={index} className="bg-white rounded-3xl border border-gray-100 shadow-[0_4px_20px_rgb(0,0,0,0.03)] overflow-hidden transition-all duration-300 hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)]">
                      {/* Section Header */}
                      <div className="bg-gray-50/80 px-6 py-5 border-b border-gray-100 flex justify-between items-center flex-wrap gap-4">
                        <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                          <span className="w-2 h-6 bg-indigo-500 rounded-full mr-1"></span>
                          {sectionResult.section}
                        </h2>
                        {sectionResult.aiPending ? (
                          <div className="bg-indigo-50 text-indigo-700 px-3 py-1 rounded-lg text-xs font-bold animate-pulse">
                            ✨ AI Scoring...
                          </div>
                        ) : sectionResult.aiError ? (
                          <div className="bg-red-50 text-red-700 px-3 py-1 rounded-lg text-xs font-bold">
                            ⚠️ Failed
                          </div>
                        ) : sectionResult.section.includes('Typing') ? (
                          <div className="bg-white px-4 py-2 rounded-xl border border-gray-200 shadow-sm flex items-center gap-3">
                            <span className="text-sm text-gray-500 font-medium">Speed:</span>
                            <span className="text-lg font-black text-indigo-600">{sectionResult.wpm} WPM</span>
                            <span className="w-px h-4 bg-gray-300 mx-1"></span>
                            <span className="text-sm text-gray-500 font-medium">Accuracy:</span>
                            <span className="text-lg font-black text-green-600">{sectionResult.accuracy}%</span>
                          </div>
                        ) : (
                          typeof sectionResult.score !== 'undefined' && sectionResult.score !== null && (
                            <div className="bg-white px-5 py-2 rounded-xl border border-gray-200 shadow-sm flex items-baseline gap-2">
                              <span className="text-xl font-black text-indigo-600">{sectionResult.score}</span>
                              <span className="text-gray-400 font-bold">/ {sectionResult.total}</span>
                            </div>
                          )
                        )}
                      </div>

                      {/* Questions List */}
                      <div className="p-6 space-y-4">
                        {sectionResult.aiPending ? (
                          <div className="flex flex-col items-center justify-center text-center py-10 bg-indigo-50/10 rounded-2xl border border-dashed border-indigo-100">
                            <div className="w-8 h-8 border-3 border-indigo-100 border-t-indigo-500 rounded-full animate-spin mb-3"></div>
                            <p className="text-indigo-800 font-bold text-sm">✨ AI Scoring in progress...</p>
                            <p className="text-gray-400 text-xs mt-1">This will update automatically in a few seconds.</p>
                          </div>
                        ) : sectionResult.aiError ? (
                          <div className="flex flex-col items-center justify-center text-center py-10 bg-red-50/20 rounded-2xl border border-dashed border-red-100">
                            <p className="text-red-700 font-bold text-sm">⚠️ AI Evaluation failed due to high demand.</p>
                            <p className="text-gray-500 text-xs mt-1 mb-4">Your responses have been saved safely.</p>
                            <button
                              onClick={() => handleRetrySection(sectionResult.section)}
                              className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-xs shadow-sm transition-colors cursor-pointer"
                            >
                              Retry AI Evaluation
                            </button>
                          </div>
                        ) : sectionResult.section.includes('Typing') ? (
                          <div className="space-y-6">
                            {/* Grid of stats */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                              <div className="bg-gradient-to-br from-indigo-50 to-indigo-100/50 p-5 rounded-2xl border border-indigo-100 flex flex-col items-center justify-center text-center shadow-sm">
                                <span className="text-xs uppercase tracking-wider font-extrabold text-indigo-500/80 block mb-1">Time Duration</span>
                                <span className="text-2xl font-black text-indigo-900">1:00 min</span>
                              </div>
                              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 p-5 rounded-2xl border border-emerald-100 flex flex-col items-center justify-center text-center shadow-sm">
                                <span className="text-xs uppercase tracking-wider font-extrabold text-emerald-600/80 block mb-1">Right Words</span>
                                <span className="text-2xl font-black text-emerald-900">{sectionResult.rightWords ?? Math.round((sectionResult.wpm * (sectionResult.accuracy / 100)) || 0)}</span>
                              </div>
                              <div className="bg-gradient-to-br from-rose-50 to-rose-100/50 p-5 rounded-2xl border border-rose-100 flex flex-col items-center justify-center text-center shadow-sm">
                                <span className="text-xs uppercase tracking-wider font-extrabold text-rose-600/80 block mb-1">Wrong Words</span>
                                <span className="text-2xl font-black text-rose-900">{sectionResult.wrongWords ?? 0}</span>
                              </div>
                              <div className="bg-gradient-to-br from-amber-50 to-amber-100/50 p-5 rounded-2xl border border-amber-100 flex flex-col items-center justify-center text-center shadow-sm">
                                <span className="text-xs uppercase tracking-wider font-extrabold text-amber-600/80 block mb-1">Total Key Press</span>
                                <span className="text-2xl font-black text-amber-900">{sectionResult.keystrokes ?? Math.round((sectionResult.wpm * 5) || 0)}</span>
                              </div>
                            </div>

                            {/* Reference vs Typed Comparison */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                              <div className="p-5 rounded-2xl bg-gray-50 border border-gray-100">
                                <span className="text-xs uppercase tracking-wider font-extrabold text-gray-400 block mb-3">Reference Text</span>
                                <p className="text-gray-700 leading-relaxed font-medium select-none bg-white p-4 rounded-xl border border-gray-100 shadow-sm text-sm">
                                  {sectionResult.paragraph || (sectionResult.questions?.[0]?.question || '')}
                                </p>
                              </div>
                              <div className="p-5 rounded-2xl bg-indigo-50/20 border border-indigo-100/30">
                                <span className="text-xs uppercase tracking-wider font-extrabold text-indigo-400 block mb-3">Your Answer</span>
                                <p className="text-indigo-950 leading-relaxed font-semibold bg-white p-4 rounded-xl border border-indigo-50 shadow-sm text-sm break-words">
                                  {sectionResult.userAnswer || (sectionResult.questions?.[0]?.userAnswer || 'No Answer Given')}
                                </p>
                              </div>
                            </div>
                          </div>
                        ) : (
                          sectionResult.questions && sectionResult.questions.map((res, qIndex) => (
                            <div key={qIndex} className="p-5 rounded-2xl bg-gray-50/50 border border-gray-100 hover:border-indigo-100 transition-colors">
                              <div className="flex items-start gap-4">
                                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 font-bold flex items-center justify-center text-sm shadow-sm">
                                  {qIndex + 1}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold text-gray-800 mb-3 text-lg leading-snug">
                                    {res.question?.replace('___', '...')?.replace(/\//g, ' ')}
                                  </p>

                                  <div className={`inline-block px-4 py-3 rounded-xl border mb-3 shadow-sm w-full sm:w-auto ${res.score === 0 || res.isCorrect === false ? 'bg-red-50/50 border-red-100' : 'bg-white border-gray-200'}`}>
                                    <span className="text-xs uppercase tracking-wider font-bold text-gray-400 block mb-1">Your Answer</span>
                                    <span className={`font-semibold text-base break-words ${res.score === 0 || res.isCorrect === false ? 'text-red-700' : 'text-gray-900'}`}>
                                      {res.userAnswer || "No Answer Given"}
                                    </span>
                                  </div>

                                  {/* Sub-score display for Passage/Email */}
                                  {typeof res.score !== 'undefined' && res.score !== null && (
                                    <div className="mt-1 mb-3 inline-flex items-center gap-2 text-sm text-indigo-700 font-bold bg-indigo-50/80 px-3 py-1.5 rounded-lg border border-indigo-100">
                                      Score: {res.score}/10
                                      {res.matchPercentage && <span className="text-indigo-500 font-medium ml-2 border-l border-indigo-200 pl-2">Keywords Matched: {res.matchPercentage}%</span>}
                                    </div>
                                  )}

                                  {/* AI Feedback Display */}
                                  {res.aiFeedback && !res.aiFeedback.error && (
                                    <div className="mt-4 overflow-hidden rounded-2xl border border-indigo-100/50 bg-gradient-to-r from-indigo-50/30 to-violet-50/30">
                                      <div className="bg-indigo-50/80 px-4 py-2 border-b border-indigo-100/50 flex items-center gap-2">
                                        <span className="text-xl">✨</span>
                                        <h4 className="text-indigo-900 font-black text-sm uppercase tracking-wide">AI Analysis</h4>
                                      </div>
                                      <div className="p-4 text-sm text-indigo-900/80 space-y-3">
                                        {res.aiFeedback.grammar_feedback && (
                                          <div className="flex items-start gap-2">
                                            <span className="font-bold text-indigo-900 whitespace-nowrap">Feedback:</span>
                                            <span className="leading-relaxed">{res.aiFeedback.grammar_feedback}</span>
                                          </div>
                                        )}
                                        {res.aiFeedback.missing_points && res.aiFeedback.missing_points.length > 0 && (
                                          <div>
                                            <span className="font-bold text-indigo-900 block mb-1">Missing Points:</span>
                                            <div className="flex flex-wrap gap-2">
                                              {res.aiFeedback.missing_points.map((pt, k) => (
                                                <span key={k} className="px-2.5 py-1 bg-white rounded-lg border border-indigo-100 text-indigo-700 text-xs font-semibold shadow-sm">{pt}</span>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        {!res.aiFeedback.grammar_feedback && !res.aiFeedback.missing_points && (
                                          <p className="italic">No specific improvements found.</p>
                                        )}
                                        {res.aiFeedback.ideal_response && (
                                          <div className="mt-4 pt-4 border-t border-indigo-100/50">
                                            <span className="font-bold text-indigo-900 block mb-2 flex items-center gap-2"><FaStar className="text-yellow-500" /> Optimal Response</span>
                                            <div className="bg-white p-3 rounded-xl border border-indigo-50 shadow-sm text-indigo-950 font-medium italic leading-relaxed">
                                              "{res.aiFeedback.ideal_response}"
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Correct / Expected Answer Display Badge */}
                                  {typeof res.isCorrect !== 'undefined' ? (
                                    res.isCorrect ? (
                                      <div className="mt-3 text-xs font-bold flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-emerald-800 bg-emerald-50/90 border border-emerald-200 p-3 rounded-xl shadow-sm">
                                        <div className="flex items-center gap-1.5">
                                          <FaCheckCircle className="text-emerald-500 text-sm flex-shrink-0" />
                                          <span>Status: <span className="font-extrabold text-emerald-900">Correct Answer</span></span>
                                        </div>
                                        {res.correctAnswer && res.correctAnswer !== res.userAnswer && (
                                          <span className="text-[11px] font-semibold text-emerald-700 bg-white/80 px-2 py-1 rounded border border-emerald-100">
                                            Model Answer: {res.correctAnswer}
                                          </span>
                                        )}
                                      </div>
                                    ) : (
                                      <div className="mt-3 text-xs font-bold flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-rose-800 bg-rose-50/90 border border-rose-200 p-3 rounded-xl shadow-sm">
                                        <div className="flex items-center gap-1.5">
                                          <span className="text-rose-500 font-black text-sm">✕</span>
                                          <span>Status: <span className="font-extrabold text-rose-900">Incorrect</span></span>
                                        </div>
                                        {res.correctAnswer && (
                                          <span className="text-[11px] font-extrabold text-rose-900 bg-white px-2.5 py-1 rounded border border-rose-200 shadow-xs">
                                            Expected Answer: {res.correctAnswer}
                                          </span>
                                        )}
                                      </div>
                                    )
                                  ) : (
                                    res.correctAnswer && (
                                      <div className="mt-3 text-xs font-bold flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-indigo-900 bg-indigo-50/90 border border-indigo-200 p-3 rounded-xl shadow-sm">
                                        <div className="flex items-center gap-1.5">
                                          <FaStar className="text-amber-500 text-sm flex-shrink-0" />
                                          <span>Model Target / Expected Answer</span>
                                        </div>
                                        <span className="text-[11px] font-extrabold text-indigo-950 bg-white px-2.5 py-1 rounded border border-indigo-200 shadow-xs leading-normal">
                                          Expected Answer: {res.correctAnswer}
                                        </span>
                                      </div>
                                    )
                                  )}
                                </div>
                              </div>
                            </div>
                          ))
                        )}

                        {/* Full Email AI Advanced Card */}
                        {sectionResult.aiAnalysis && !sectionResult.aiAnalysis.error && (sectionResult.section.includes('Email Writing') || sectionResult.section.includes('Summary & Opinion')) && (
                          <div className="mt-6 mb-2 rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 to-fuchsia-50 overflow-hidden shadow-lg">
                            <div className="bg-violet-900 px-6 py-4 flex items-center justify-between text-white">
                              <h4 className="font-black flex items-center gap-2 text-lg">
                                <span className="text-2xl">🤖</span> AI Advanced Grader
                              </h4>
                              <span className="px-3 py-1 bg-violet-700 rounded-full text-xs font-bold tracking-wider uppercase border border-violet-500 shadow-inner">
                                CEFR Level: {sectionResult.aiAnalysis.cefr_level || 'N/A'}
                              </span>
                            </div>

                            <div className="p-6">
                                <div className="space-y-5 text-violet-950">
                                  <div className="bg-white/60 p-4 rounded-xl border border-violet-100">
                                    <strong className="block text-violet-900 mb-1 text-sm uppercase tracking-wide">Analysis</strong>
                                    <p className="leading-relaxed">{sectionResult.aiAnalysis.feedback || "No feedback generated."}</p>
                                  </div>

                                  <div className="flex items-center gap-2 text-sm font-bold bg-white/60 w-max px-4 py-2 rounded-xl border border-violet-100">
                                    <span className="text-violet-500 uppercase tracking-widest text-xs">Tone</span>
                                    {sectionResult.aiAnalysis.tone_analysis || "Not analyzed"}
                                  </div>

                                  {sectionResult.aiAnalysis.corrections && sectionResult.aiAnalysis.corrections.length > 0 && (
                                    <div>
                                      <strong className="block text-violet-900 mb-2 text-sm uppercase tracking-wide">Corrections Made</strong>
                                      <ul className="space-y-2">
                                        {sectionResult.aiAnalysis.corrections.map((c, k) => (
                                          <li key={k} className="flex items-start gap-2 bg-white/80 p-3 rounded-xl shadow-sm border border-violet-100 text-sm">
                                            <span className="w-5 h-5 rounded-full bg-violet-100 text-violet-600 flex flex-shrink-0 items-center justify-center font-bold text-xs mt-0.5">{k + 1}</span>
                                            <span className="leading-relaxed">{c}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}

                                  {sectionResult.aiAnalysis.ideal_response && (
                                    <div className="mt-6 pt-6 border-t border-violet-200">
                                      <strong className="block text-violet-900 mb-3 text-sm uppercase tracking-wide flex items-center gap-2">
                                        <FaStar className="text-yellow-500 text-lg" /> Improved AI Draft
                                      </strong>
                                      <div className="italic text-violet-900 bg-white p-5 rounded-2xl shadow-sm border border-violet-100 whitespace-pre-wrap font-serif leading-relaxed text-lg">
                                        {sectionResult.aiAnalysis.ideal_response}
                                      </div>
                                    </div>
                                  )}
                                </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* eBook Promo Banner (Only shown if user does not have access) */}
              {!hasEbookAccess && (
                <div className="mt-12 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100 p-6 sm:p-8 rounded-[2.5rem] flex flex-col md:flex-row items-center justify-between gap-6 shadow-md text-left">
                  <div className="flex items-start gap-4 sm:gap-6">
                    <img 
                      src="/images/versant_ebook_cover.jpg" 
                      alt="Versant Ebook Cover" 
                      onClick={() => navigate('/ebook')}
                      className="w-24 h-auto rounded-xl shadow-md border border-emerald-100 flex-shrink-0 cursor-pointer hover:scale-105 transition-transform"
                    />
                    <div className="space-y-2">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-100 text-emerald-800 text-xs font-bold uppercase rounded-full tracking-wider">
                        📘 RECOMMENDED RESOURCE
                      </span>
                      <h3 className="text-xl sm:text-2xl font-black text-gray-900 leading-tight">
                        Versant Test Mastery Ebook
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed max-w-xl">
                        Scored low? Get the exact speaking templates, secret voice adjustments, and sample answers that candidates use to pass Amazon & Deloitte screenings.
                      </p>
                      <div className="text-emerald-700 font-bold text-sm">
                        Available now for just {currency === 'INR' ? '₹199' : currency === 'EUR' ? '€2.99' : '$2.99'} (One-time payment)
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/ebook')}
                    className="w-full md:w-auto px-6 py-4 bg-emerald-600 hover:bg-emerald-750 text-white font-extrabold rounded-xl transition-all hover:scale-[1.02] shadow-md flex items-center justify-center gap-2 whitespace-nowrap text-sm"
                  >
                    View Ebook Guide &rarr;
                  </button>
                </div>
              )}

              {/* Action Buttons & Conversion CTA */}
              <div className="mt-16 bg-gradient-to-b from-gray-900 to-black p-8 sm:p-12 rounded-[2.5rem] border border-gray-800 flex flex-col items-center justify-center text-center shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 -mr-10 -mt-10 opacity-10">
                    <FaTrophy className="text-[15rem] text-white" />
                </div>
                
                {isBelowCutoff ? (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6 mb-8 text-left max-w-3xl relative">
                      <div className="absolute top-0 right-0 -mr-2 -mt-2 w-4 h-4 bg-red-500 rounded-full animate-ping"></div>
                      <h3 className="text-xl font-bold text-red-400 flex items-center gap-2 mb-3">
                          <FaExclamationTriangle className="text-2xl" /> Warning: {isPracticeMode ? 'Accuracy Target Not Met' : 'Critical Score'}
                      </h3>
                      <p className="text-gray-300 text-base md:text-lg leading-relaxed">
                          {isPracticeMode ? (
                            <>
                              Your practice accuracy is <span className="font-bold text-white">{Math.round((score / maxScore) * 100)}%</span>. Top MNCs require a consistent accuracy of <span className="font-bold text-emerald-400">80%+</span> across all sections. Keep practicing to perfect this module.
                            </>
                          ) : (
                            <>
                              Your current score of <span className="font-bold text-white">{score}</span> falls below the strict 65-point cutoff used by Amazon, Deloitte, and top MNCs. Candidates who fail this stage are <span className="font-bold text-red-400">immediately rejected without human review</span>.
                            </>
                          )}
                      </p>
                  </div>
                ) : (
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-6 mb-8 text-left max-w-3xl relative">
                      <h3 className="text-xl font-bold text-emerald-400 flex items-center gap-2 mb-3">
                          <FaCheckCircle className="text-2xl" /> Success: {isPracticeMode ? 'Accuracy Target Achieved' : 'MNC Interview Ready'}
                      </h3>
                      <p className="text-gray-300 text-base md:text-lg leading-relaxed">
                          {isPracticeMode ? (
                            <>
                              Excellent! Your practice accuracy is <span className="font-bold text-white">{Math.round((score / maxScore) * 100)}%</span>, which meets the standard for top MNCs. Keep practicing to maintain this level of fluency!
                            </>
                          ) : (
                            <>
                              Congratulations! Your score of <span className="font-bold text-white">{score}</span> is above the MNC cutoff of 65. You are ready to pass top hiring screening rounds!
                            </>
                          )}
                      </p>
                  </div>
                )}
                
                <h2 className="text-2xl md:text-4xl font-black text-white mb-6 z-10">
                  {isBelowCutoff ? "Don't let this cost you your dream job." : "Lock in your success and secure your dream job."}
                </h2>
                <p className="text-gray-400 text-lg md:text-xl max-w-2xl mx-auto mb-10 z-10 font-medium">
                  {isBelowCutoff 
                    ? "Unlock all 38 Total Mock Exams (19 Reading/Writing + 19 Speaking & Listening), practice section-wise, or get our step-by-step Mastery eBook to guarantee you pass."
                    : "Access all 38 Total Mock Exams (19 Reading/Writing + 19 Speaking & Listening), targeted practice section-packs, or get our step-by-step Mastery eBook to lock in your preparation."
                  }
                </p>

                <div className="z-10 w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                  {/* ⚡ 45-Day Sprint Pass Card */}
                  <Link
                    to="/pricing"
                    className="flex-1 inline-flex flex-col items-center justify-center bg-gradient-to-br from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-slate-950 p-5 rounded-2xl transition-all shadow-[0_0_25px_rgba(20,184,166,0.3)] hover:shadow-[0_0_40px_rgba(20,184,166,0.5)] hover:-translate-y-1 text-center"
                  >
                    <span className="text-[10px] uppercase font-black tracking-widest text-slate-950 mb-1">🎯 Best For Upcoming Test</span>
                    <div className="flex items-center justify-center gap-1.5 font-black text-sm lg:text-base mb-1 whitespace-nowrap">
                      ⚡ 45-Day Sprint Pass
                    </div>
                    <span className="text-xs font-bold opacity-90 block">38 Mock Exams (45 Days) • {currency === 'INR' ? '₹699' : currency === 'EUR' ? '€6.99' : '$6.99'}</span>
                  </Link>

                  {/* 🏆 Lifetime VIP Pass Card */}
                  <Link
                    to="/pricing"
                    className="flex-1 inline-flex flex-col items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-400 hover:from-emerald-400 hover:to-teal-300 text-gray-900 p-5 rounded-2xl transition-all shadow-[0_0_30px_rgba(16,185,129,0.25)] hover:shadow-[0_0_50px_rgba(16,185,129,0.45)] hover:-translate-y-1 text-center"
                  >
                    <span className="text-[10px] uppercase font-black tracking-widest text-emerald-950 mb-1">⭐ Best Value (Lifetime)</span>
                    <div className="flex items-center justify-center gap-1.5 font-black text-sm lg:text-base mb-1 whitespace-nowrap">
                      <FaStar className="animate-pulse flex-shrink-0 text-amber-950" /> Lifetime VIP Pass
                    </div>
                    <span className="text-xs font-bold opacity-90 block">Lifetime Access + eBook • {currency === 'INR' ? '₹1,449' : currency === 'EUR' ? '€14.99' : '$14.99'}</span>
                  </Link>

                  {/* 🎯 Unlock Individual Section Packs Card */}
                  <Link
                    to="/pricing"
                    className="flex-1 inline-flex flex-col items-center justify-center bg-gradient-to-br from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white p-5 rounded-2xl transition-all shadow-[0_0_30px_rgba(99,102,241,0.25)] hover:-translate-y-1 text-center"
                  >
                    <span className="text-[10px] uppercase font-black tracking-widest text-indigo-200 mb-1">Targeted Practice</span>
                    <div className="flex items-center justify-center gap-1.5 font-black text-sm lg:text-base mb-1 whitespace-nowrap">
                      🎯 Section-wise Pack
                    </div>
                    <span className="text-xs font-bold opacity-90 block">Unlock 1 section • {currency === 'INR' ? '₹299' : currency === 'EUR' ? '€2.99' : '$2.99'}</span>
                  </Link>

                  {/* 📘 eBook Card */}
                  {!hasEbookAccess && (
                    <Link
                      to="/ebook"
                      className="flex-1 inline-flex flex-col items-center justify-center bg-gradient-to-br from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-slate-950 p-5 rounded-2xl transition-all shadow-[0_0_20px_rgba(245,158,11,0.35)] hover:-translate-y-1 text-center"
                    >
                      <span className="text-[10px] uppercase font-black tracking-widest text-amber-950 mb-1">Secret Templates</span>
                      <div className="flex items-center justify-center gap-1.5 font-black text-sm lg:text-base mb-1 whitespace-nowrap">
                        📘 Versant eBook
                      </div>
                      <span className="text-xs font-bold opacity-90 block">Score-boosting strategy • {currency === 'INR' ? '₹199' : currency === 'EUR' ? '€2.99' : '$2.99'}</span>
                    </Link>
                  )}
                </div>

                <div className="flex flex-wrap items-center justify-center gap-3 lg:gap-4 z-10 w-full">
                    <Link
                      to="/dashboard"
                      className="inline-flex items-center justify-center bg-gray-800 text-gray-300 font-bold py-3 px-6 sm:px-8 rounded-xl hover:bg-gray-700 transition-all hover:-translate-y-1 text-sm border border-gray-700 w-full sm:w-auto"
                    >
                      Return to Dashboard
                    </Link>
                    <button 
                      onClick={handleNativeShare}
                      className="inline-flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-blue-400 font-bold py-3 px-6 sm:px-8 rounded-xl transition-all hover:-translate-y-1 text-sm border border-gray-700 w-full sm:w-auto"
                    >
                       Share Target Score 
                    </button>
                    <a 
                      href={`https://wa.me/?text=${encodeURIComponent(`I just scored ${score}/80 on the Versant AI test! Take a free demo and check your fluency level: https://versantpro.com`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-emerald-400 font-bold py-3 px-6 sm:px-8 rounded-xl transition-all hover:-translate-y-1 text-sm border border-gray-700 w-full sm:w-auto"
                    >
                       WhatsApp
                    </a>
                </div>
              </div>
            </div>
          </div>
          
          {/* Google & Email Auth Trap Modal Overlay */}
          {isLocked && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm animate-fade-in overflow-y-auto">
              <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md overflow-hidden relative border border-gray-100 py-8 px-6 text-center my-6">
                <div className="absolute top-0 left-0 h-2 bg-indigo-500 w-full"></div>
                
                <h2 className="text-2xl font-black text-gray-900 mb-2 tracking-tight">Save Your Progress</h2>
                <p className="text-gray-500 mb-6 font-medium text-sm">Create a free account or log in to unlock your detailed AI performance report and save your score forever.</p>
                
                <button 
                  onClick={handleGoogleSignIn}
                  className="w-full flex justify-center items-center gap-3 bg-white border border-gray-300 rounded-xl px-4 py-3.5 text-gray-800 font-bold hover:bg-gray-50 hover:border-gray-400 hover:shadow-md transition-all active:scale-95"
                >
                  <svg className="w-5 h-5" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.238-2.626-.611-3.917z"/><path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.222 0-9.649-3.342-11.124-7.854l-6.571 4.819C9.656 39.663 16.318 44 24 44z"/><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.237-2.626-.611-3.917z"/></svg>
                  Continue with Google
                </button>

                <div className="flex items-center my-5">
                  <div className="flex-1 border-t border-gray-200"></div>
                  <span className="px-3 text-gray-400 text-xs font-bold uppercase tracking-widest">OR</span>
                  <div className="flex-1 border-t border-gray-200"></div>
                </div>

                <form onSubmit={handleEmailAuth} className="space-y-4 text-left">
                  {authError && <div className="text-red-500 text-xs font-bold text-center bg-red-50 py-2 rounded-lg">{authError}</div>}
                  <div>
                    <input 
                      type="email" 
                      required
                      value={authEmail}
                      onChange={(e) => setAuthEmail(e.target.value)}
                      placeholder="Email Address" 
                      className="w-full px-4 py-3.5 bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm font-medium"
                    />
                  </div>
                  <div>
                     <input 
                      type="password" 
                      required
                      value={authPassword}
                      onChange={(e) => setAuthPassword(e.target.value)}
                      placeholder="Password" 
                      className="w-full px-4 py-3.5 bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm font-medium"
                    />
                  </div>
                  <button type="submit" className="w-full py-4 bg-gray-900 hover:bg-black text-white font-bold rounded-xl shadow-lg transition-all active:scale-95 text-sm">
                    {isLoginView ? 'Log In & View Score' : 'Create Account & View Score'}
                  </button>
                </form>
                
                <p className="mt-5 text-sm outline-none">
                  {isLoginView ? "Don't have an account? " : "Already have an account? "}
                  <button onClick={() => {setIsLoginView(!isLoginView); setAuthError('');}} className="font-bold text-indigo-600 hover:text-indigo-800 underline focus:outline-none">
                    {isLoginView ? "Sign up" : "Log in"}
                  </button>
                </p>

                <div className="mt-6 text-gray-400 text-xs font-semibold flex items-center justify-center gap-1">
                   <FaCheckCircle className="text-indigo-500"/> Guaranteed Spam-Free
                </div>
              </div>
            </div>
          )}

        </main>
      )}
      <Footer />
    </div>
  );
}

export default ResultPage;